import { useState, useEffect, useRef, useCallback, useMemo, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, getAccessToken, errMsg, errStatus } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { DatePicker } from "../components/DatePicker.js";
import { useLookup, useLookupRefresh } from "../context/LookupContext.js";
import { usePageBack } from "../context/PageBackContext.js";
import { DropdownSelect } from "../components/DropdownButton.js";
import { Card } from "../components/ui/card.js";
import { Button } from "../components/ui/button.js";
import { AsyncButton } from "../components/ui/async-button.js";
import { Badge } from "../components/ui/badge.js";
import { Input } from "../components/ui/input.js";
import { ScrollSlider } from "../components/ui/scroll-slider.js";
import { FileInput } from "../components/ui/file-input.js";
import { AssetImportPanel } from "../components/AssetImportPanel.js";
import { AssetDisposalPanel } from "../components/AssetDisposalPanel.js";
import { getCategoryIcon } from "../lib/assetCategoryIcons.js";

import { ASSET_ROLES, FINANCE_ROLES, ASSET_ADMIN_ROLES } from "@hrms/types";
import { Camera, X, CheckCircle2 } from "lucide-react";
import { SpecularButton } from "../components/ui/specular-button.js";

function decodeRole(token: string): string {
  try { return String((JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>)["role"] ?? ""); }
  catch { return ""; }
}
function decodeEmployeeId(token: string): string {
  try { return String((JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>)["employeeId"] ?? ""); }
  catch { return ""; }
}
function decodeOfficeId(token: string): string {
  try {
    const v = (JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>)["officeId"];
    return typeof v === "string" ? v : "";
  } catch { return ""; }
}

// ── Types ──────────────────────────────────────────────────────────────────────

interface Office { id: string; name: string; city: string }

interface Asset {
  id: string; assetTag: string; name: string; category: string; make: string | null;
  model: string | null; serialNumber: string | null; macAddress: string | null;
  status: string; condition: string; currency: string;
  purchasePrice: string | null; warrantyExpiry: string | null;
  vendor: string | null; poReference: string | null; notes: string | null;
  ownershipType: string; rentalProvider: string | null;
  rentalStartDate: string | null; rentalEndDate: string | null;
  rentalMonthlyCost: string | null; rentalCurrency: string;
  officeId: string | null; office: Office | null;
  location: string | null;
  licenseType: string | null; licenseSeats: number | null;
  licenseKey: string | null; licenseRenewalOwner: string | null;
  telecomCarrier: string | null; telecomPhoneNumber: string | null;
  telecomSimNumber: string | null; telecomPlanName: string | null;
  telecomMonthlyCharge: string | null; telecomLockStatus: string | null;
  assignments: Array<{
    id: string; assignedAt: string; conditionAtIssue: string | null;
    expectedReturnDate: string | null;
    employee: { id: string; firstName: string; lastName: string; employeeCode: string };
  }>;
}

interface StockSummaryRow { officeId: string | null; category: string; model: string | null; count: number }

interface ExpiringData { expired: Asset[]; expiringSoon: Asset[] }

interface AssignmentHistoryItem {
  id: string; assignedAt: string; returnedAt: string | null;
  conditionAtIssue: string | null; conditionAtReturn: string | null;
  photoBeforeS3Key: string | null; photoAfterS3Key: string | null;
  acknowledgedAt: string | null;
  employee: { id: string; firstName: string; lastName: string; employeeCode: string };
}

interface StatusHistoryItem {
  id: string; fromStatus: string | null; toStatus: string; changedAt: string; notes: string | null;
  changedBy: { id: string; firstName: string; lastName: string; employeeCode: string };
}

interface MaintenanceRecordItem {
  id: string; vendor: string | null; cost: string | null; currency: string;
  faultDescription: string; sentOutAt: string; returnedAt: string | null; notes: string | null;
  createdBy: { id: string; firstName: string; lastName: string; employeeCode: string };
}

interface MyAsset {
  id: string; assetId: string; assignedAt: string; conditionAtIssue: string | null;
  expectedReturnDate: string | null; acknowledgedAt: string | null;
  asset: { assetTag: string; name: string; category: string; condition: string };
}

interface ITTicket {
  id: string; title: string; ticketType: string; category: string;
  priority: string; status: string; description: string | null;
  createdAt: string; resolvedAt: string | null; slaTargetAt: string | null;
  resolutionNotes: string | null;
  employee: { id: string; firstName: string; lastName: string; employeeCode: string };
  assignedTo: { id: string; firstName: string; lastName: string } | null;
  linkedAsset: { id: string; assetTag: string; name: string; category: string } | null;
  _count?: { comments: number };
}

interface TicketComment {
  id: string; body: string; createdAt: string;
  author: { id: string; firstName: string; lastName: string };
}

interface EmployeeHit {
  id: string; firstName: string; lastName: string; employeeCode: string;
}

interface VerificationFlag {
  field: string; severity: "info" | "warning" | "critical"; message: string;
  claimed: string | null; extracted: string | null;
}
interface VerificationResult {
  verdict: "CLEAN" | "FLAGGED"; flags: VerificationFlag[]; checkedAt: string;
}

interface StockRequest {
  id: string; category: string; model: string | null; quantity: number;
  estimatedUnitCost: string; currency: string; justification: string;
  status: string; financeRemarks: string | null;
  createdAt: string; financeActionAt: string | null;
  invoiceS3Key: string | null; poS3Key: string | null; vendor: string | null; poReference: string | null;
  assetTagPrefix: string | null; arrivedAt: string | null; custodyConfirmedAt: string | null;
  custodyRevokedAt: string | null; custodyRevokeReason: string | null;
  custodyRevokedBy: { id: string; firstName: string; lastName: string; employeeCode: string } | null;
  aiVerificationResult: VerificationResult | null; aiVerifiedAt: string | null;
  office: Office;
  linkedTicket: { id: string; title: string; ticketType: string } | null;
  requestedBy: { id: string; firstName: string; lastName: string; employeeCode: string };
  financeActionBy: { id: string; firstName: string; lastName: string; employeeCode: string } | null;
  receivedBy: { id: string; firstName: string; lastName: string; employeeCode: string } | null;
}

interface VerificationTask {
  id: string; campaignId?: string; status: string; notes: string | null;
  extraDescription: string | null; respondedAt: string | null;
  resolvedAt: string | null; resolutionNotes: string | null; raisedTicketId: string | null;
  createdAt: string;
  employee?: { id: string; firstName: string; lastName: string; employeeCode: string };
  assignment: { id: string; asset: { id: string; assetTag: string; name: string; category: string } } | null;
  campaign?: { id: string; title: string };
}
interface Campaign {
  id: string; title: string; description: string | null; status: string;
  officeId: string | null; office: Office | null; startedAt: string; closedAt: string | null;
  startedBy: { id: string; firstName: string; lastName: string };
  statusCounts?: Record<string, number>;
  taskCount?: number; participantCount?: number; notified?: number;
  tasks?: VerificationTask[];
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const CONDITIONS = ["NEW","GOOD","FAIR","POOR","DAMAGED","NON_FUNCTIONAL"] as const;
const TICKET_TYPES = [
  { value: "NEW_EQUIPMENT",     label: "New Equipment" },
  { value: "REPLACEMENT_REPAIR",label: "Replacement / Repair" },
  { value: "GENERAL_ISSUE",     label: "General Issue" },
  { value: "ACCESS_REQUEST",    label: "Access Request" },
  { value: "ONBOARDING_BUNDLE", label: "Onboarding Kit" },
  { value: "OTHER",             label: "Other" },
] as const;
const ASSET_STATUSES = ["ON_ORDER","RECEIVED","IN_FINANCE_CUSTODY","AVAILABLE","ASSIGNED","RETURNED",
  "IN_REPAIR","LOST","STOLEN","RETIRED","DISPOSED","RECYCLED"] as const;
const TICKET_STATUSES = ["OPEN","IN_PROGRESS","ON_HOLD","RESOLVED","CLOSED","CANCELLED"] as const;

/* Filter-bar option lists. Built once at module scope rather than per render:
   DropdownSelect only needs value + label, and none of these depend on state. */
const spaced = (v: string) => ({ value: v, label: v.replace(/_/g, " ") });
const ASSET_STATUS_OPTIONS = ASSET_STATUSES.map(spaced);
const TICKET_STATUS_OPTIONS = TICKET_STATUSES.map(spaced);
const TICKET_PRIORITY_OPTIONS = ["LOW","MEDIUM","HIGH","CRITICAL"].map(p => ({ value: p, label: p }));
const TICKET_TYPE_OPTIONS = TICKET_TYPES.map(t => ({ value: t.value, label: t.label }));
const STOCK_REQUEST_STATUS_OPTIONS =
  ["SUBMITTED","FINANCE_APPROVED","FINANCE_REJECTED","RECEIVED","CANCELLED"].map(spaced);

/* Form option lists. Same values the <option> children carried. */
const plain = (v: string) => ({ value: v, label: v });
const CURRENCY_OPTIONS = ["INR", "USD"].map(plain);
const CONDITION_OPTIONS = CONDITIONS.map(plain);
const INITIAL_STATUS_OPTIONS = ["ON_ORDER", "RECEIVED", "AVAILABLE"].map(plain);
const OWNERSHIP_OPTIONS = [
  { value: "COMPANY", label: "Company-owned" },
  { value: "RENTAL", label: "Rental" },
  { value: "CUSTOMER_PROVIDED", label: "Customer-provided" },
  { value: "SHARED", label: "Shared" },
];
const REASON_CODE_OPTIONS =
  ["LOST", "STOLEN", "DAMAGED", "MALFUNCTIONING", "UPGRADE_DUE", "END_OF_LIFE"].map(plain);
const ACCESS_LEVEL_OPTIONS = ["READ", "WRITE", "ADMIN"].map(plain);
const BUNDLE_CATEGORY_OPTIONS = ["LAPTOP","DESKTOP","MONITOR","KEYBOARD","MOUSE","HEADSET","MOBILE",
  "PERIPHERAL","NETWORKING","FURNITURE","SOFTWARE_LICENSE","OTHER"].map(plain);
const VERIFICATION_STATUSES = ["VERIFIED","DISPUTED","MISSING","DAMAGED","UNKNOWN"] as const;
const VERIFICATION_STATUS_OPTIONS = VERIFICATION_STATUSES.map(plain);

const SLA_COLOR = (iso: string | null, resolvedAt: string | null = null): React.CSSProperties => {
  if (!iso) return {};
  const against = resolvedAt ? new Date(resolvedAt).getTime() : Date.now();
  const ms = new Date(iso).getTime() - against;
  if (ms < 0)      return { background: "var(--color-error-bg)", color: "var(--color-error-fg)" };
  if (ms < 3_600_000) return { background: "var(--color-warning-border)", color: "var(--color-warning-fg-strong)" };
  return { background: "var(--color-success-bg)", color: "var(--color-success-fg)" };
};
const slaLabel = (iso: string | null, resolvedAt: string | null = null): string => {
  if (!iso) return "—";
  const against = resolvedAt ? new Date(resolvedAt).getTime() : Date.now();
  const ms = new Date(iso).getTime() - against;
  if (ms < 0) return resolvedAt ? "Missed SLA" : "BREACHED";
  const h  = Math.floor(ms / 3_600_000);
  const m  = Math.floor((ms % 3_600_000) / 60_000);
  const suffix = resolvedAt ? "left at resolution" : "left";
  return h > 24 ? `${Math.floor(h / 24)}d ${suffix}` : h > 0 ? `${h}h ${m}m ${suffix}` : `${m}m ${suffix}`;
};

const STATUS_STYLE = (s: string): React.CSSProperties => {
  if (s === "AVAILABLE" || s === "RESOLVED" || s === "CLOSED") return { background: "var(--color-success-bg)", color: "var(--color-success-fg)" };
  if (s === "ASSIGNED"  || s === "IN_PROGRESS" || s === "SUBMITTED") return { background: "var(--color-info-bg)", color: "var(--color-info-fg)" };
  if (s === "IN_REPAIR" || s === "ON_HOLD" || s === "RECEIVED" || s === "IN_FINANCE_CUSTODY") return { background: "var(--color-warning-bg)", color: "var(--color-warning-fg)" };
  if (s === "OPEN") return { background: "var(--color-warning-bg)", color: "var(--color-warning-fg)" };
  if (s === "FINANCE_APPROVED") return { background: "var(--color-success-bg)", color: "var(--color-success-fg)" };
  if (s === "LOST" || s === "STOLEN" || s === "CANCELLED" || s === "FINANCE_REJECTED") return { background: "var(--color-error-bg)", color: "var(--color-error-fg)" };
  return { background: "var(--color-surface-100)", color: "var(--color-text-secondary)" };
};
const PRIORITY_STYLE = (p: string): React.CSSProperties => {
  if (p === "CRITICAL") return { background: "var(--color-error-bg)", color: "var(--color-error-fg)" };
  if (p === "HIGH")     return { background: "var(--color-warning-border)", color: "var(--color-warning-fg-strong)" };
  if (p === "MEDIUM")   return { background: "var(--color-warning-bg)", color: "var(--color-warning-fg)" };
  return { background: "var(--color-surface-100)", color: "var(--color-text-secondary)" };
};
const SEVERITY_STYLE = (s: string): React.CSSProperties => {
  if (s === "critical") return { background: "var(--color-error-bg)", color: "var(--color-error-fg)" };
  if (s === "warning")  return { background: "var(--color-warning-bg)", color: "var(--color-warning-fg)" };
  return { background: "var(--color-info-bg)", color: "var(--color-info-fg)" };
};
const WARRANTY_STYLE = (iso: string | null): React.CSSProperties => {
  if (!iso) return {};
  const days = (new Date(iso).getTime() - Date.now()) / 86_400_000;
  return days < 0 ? { color: "var(--color-error-fg)", fontWeight: 600 } : days < 90 ? { color: "var(--color-warning-fg)", fontWeight: 600 } : {};
};

const inputStyle: React.CSSProperties = {
  padding: "8px 10px", border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)", fontSize: "14px",
  background: "var(--color-surface-0)", width: "100%", boxSizing: "border-box",
};
const btnStyle  = (color = "var(--color-brand-600)", text = "#fff"): React.CSSProperties => ({
  padding: "6px 14px", background: color, color: text,
  border: "none", borderRadius: "var(--radius-md)", cursor: "pointer",
  fontWeight: 600, fontSize: "13px",
});
const badge = (style: React.CSSProperties): React.CSSProperties => ({
  display: "inline-block", fontSize: 11, padding: "2px 8px", borderRadius: 999, fontWeight: 600, ...style,
});

type Tab = "mine" | "inventory" | "tickets" | "admin" | "procurement" | "verification" | "import" | "disposals";

const BLANK_ASSET = {
  assetTag: "", name: "", category: "LAPTOP", make: "", model: "",
  serialNumber: "", macAddress: "", condition: "GOOD" as typeof CONDITIONS[number],
  status: "AVAILABLE" as typeof ASSET_STATUSES[number],
  purchaseDate: "", purchasePrice: "", currency: "INR",
  warrantyExpiry: "", vendor: "", poReference: "", notes: "",
  ownershipType: "COMPANY", rentalProvider: "", rentalStartDate: "",
  rentalEndDate: "", rentalMonthlyCost: "", rentalCurrency: "INR",
  officeId: "", location: "",
  licenseType: "", licenseSeats: "", licenseKey: "", licenseRenewalOwner: "",
  telecomCarrier: "", telecomPhoneNumber: "", telecomSimNumber: "",
  telecomPlanName: "", telecomMonthlyCharge: "", telecomLockStatus: "",
};

const BLANK_TICKET = {
  title: "", ticketType: "GENERAL_ISSUE" as string,
  category: "OTHER", priority: "MEDIUM",
  description: "", linkedAssetId: "", linkedAssetSearch: "",
  typeData: {} as Record<string, string>,
  employeeId: "",
};

const BLANK_STOCK_REQUEST = {
  officeId: "", category: "LAPTOP", model: "", quantity: "1",
  estimatedUnitCost: "", currency: "INR", justification: "", linkedTicketId: "",
};

// ── Component ──────────────────────────────────────────────────────────────────

export default function AssetsPage() {
  usePageBack("/dashboard");
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();
  const token      = getAccessToken() ?? "";
  const role       = decodeRole(token);
  const myEmpId    = decodeEmployeeId(token);
  const myOfficeId = decodeOfficeId(token);
  const isIT       = ASSET_ROLES.has(role);
  const isFinance  = FINANCE_ROLES.has(role);
  const isAssetAdmin = ASSET_ADMIN_ROLES.has(role);
  const isRegionalScoped = role === "REGIONAL_IT" || role === "REGIONAL_HR";
  const assetCategories = useLookup("ASSET_CATEGORY");
  const refreshLookup = useLookupRefresh();

  const [tab, setTab]             = useState<Tab>("mine");
  const [disposalPrefillAssetId, setDisposalPrefillAssetId] = useState<string | null>(null);
  const [assets, setAssets]       = useState<Asset[]>([]);
  const [myAssets, setMyAssets]   = useState<MyAsset[]>([]);
  const [tickets, setTickets]     = useState<ITTicket[]>([]);
  const [stockRequests, setStockRequests] = useState<StockRequest[]>([]);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState("");
  const [err, setErr]             = useState("");

  // Inventory filters
  // One "which dropdown is open" flag for every filter bar on the page, so
  // opening one closes any other (DropdownSelect's controlled open/onToggle).
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [filterStatus,   setFilterStatus]   = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterOffice,   setFilterOffice]   = useState("");
  const [search, setSearch]                 = useState("");
  const searchRef = useRef<ReturnType<typeof setTimeout>>();

  // Inventory type management (Manage Types panel)
  const [showManageTypes, setShowManageTypes] = useState(false);
  const [typeRows, setTypeRows] = useState<{ id: string; value: string; displayName: string; isActive: boolean; isSystem: boolean }[]>([]);
  const [newTypeValue, setNewTypeValue] = useState("");
  const [newTypeLabel, setNewTypeLabel] = useState("");

  // Offices + stock visibility
  const [offices, setOffices]           = useState<Office[]>([]);

  // The two filter lists that come from loaded data rather than a constant.
  // Asset types keep their category icon, which is why DropdownOption's label
  // is a node.
  const officeOptions = useMemo(
    () => offices.map(o => ({ value: o.id, label: `${o.name} — ${o.city}` })),
    [offices]
  );
  const categoryNameOptions = useMemo(
    () => assetCategories.map(c => ({ value: c.value, label: c.displayName })),
    [assetCategories]
  );
  const categoryOptions = useMemo(
    () => assetCategories.map(c => {
      const Icon = getCategoryIcon(c.value);
      return {
        value: c.value,
        label: <span className="flex items-center gap-2"><Icon className="h-3.5 w-3.5" />{c.displayName}</span>,
      };
    }),
    [assetCategories]
  );
  const [stockSummary, setStockSummary] = useState<StockSummaryRow[]>([]);

  // Procurement (stock requests)
  const [srFilterStatus, setSrFilterStatus] = useState("");
  const [srFilterOffice, setSrFilterOffice] = useState("");
  const [showStockRequestForm, setShowStockRequestForm] = useState(false);
  const [stockRequestForm, setStockRequestForm] = useState({ ...BLANK_STOCK_REQUEST });
  const [editingStockRequestId, setEditingStockRequestId] = useState<string | null>(null);
  const [financeRemarksDraft, setFinanceRemarksDraft] = useState<Record<string, string>>({});
  const [financeActionTarget, setFinanceActionTarget] = useState<{ id: string; action: "approve" | "reject" | "revokeCustody" } | null>(null);
  const [receivingRequestId, setReceivingRequestId] = useState<string | null>(null);
  const [receiveForm, setReceiveForm] = useState({ vendor: "", poReference: "", assetTagPrefix: "" });
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [poFile, setPoFile] = useState<File | null>(null);
  const [uploadingPo, setUploadingPo] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState<VerificationResult | null>(null);
  const [aiUnavailable, setAiUnavailable] = useState(false);

  // Ticket filters
  const [tStatus,   setTStatus]   = useState("");
  const [tPriority, setTPriority] = useState("");
  const [tType,     setTType]     = useState("");

  // Asset form
  const [showAssetForm, setShowAssetForm] = useState(false);
  const [assetForm, setAssetForm]         = useState({ ...BLANK_ASSET });

  // Assign modal
  const [assigningAsset, setAssigningAsset] = useState<Asset | null>(null);
  const [assignForm, setAssignForm]         = useState({ employeeId: "", conditionAtIssue: "GOOD", expectedReturnDate: "", notes: "" });
  const [empSearch, setEmpSearch]           = useState("");
  const [empHits, setEmpHits]               = useState<EmployeeHit[]>([]);
  const [empLoading, setEmpLoading]         = useState(false);
  const empRef = useRef<ReturnType<typeof setTimeout>>();
  const [beforePhotoFile, setBeforePhotoFile] = useState<File | null>(null);

  // Return modal
  const [returningAsset, setReturningAsset] = useState<Asset | null>(null);
  const [returnForm, setReturnForm]         = useState({ conditionAtReturn: "GOOD", returnNotes: "" });
  const [afterPhotoFile, setAfterPhotoFile] = useState<File | null>(null);

  // Transfer modal
  const [transferringAsset, setTransferringAsset] = useState<Asset | null>(null);
  const [transferForm, setTransferForm] = useState({ employeeId: "", conditionAtReturn: "GOOD", conditionAtIssue: "GOOD", expectedReturnDate: "", notes: "" });

  // Assignment history (per-asset expand)
  const [expandedAssetId, setExpandedAssetId] = useState<string | null>(null);
  const [assetHistory, setAssetHistory]       = useState<AssignmentHistoryItem[]>([]);
  const [assetStatusHistory, setAssetStatusHistory] = useState<StatusHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory]   = useState(false);
  const [assetMaintenance, setAssetMaintenance] = useState<MaintenanceRecordItem[]>([]);
  const [showMaintenanceForm, setShowMaintenanceForm] = useState(false);
  const [maintenanceForm, setMaintenanceForm] = useState({ vendor: "", cost: "", currency: "INR", faultDescription: "", sentOutAt: "", notes: "" });

  // Ticket form
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketForm, setTicketForm]         = useState({ ...BLANK_TICKET });
  const [assetSearchVal, setAssetSearchVal] = useState("");
  const [assetHits, setAssetHits]           = useState<{ id: string; assetTag: string; name: string }[]>([]);
  const [ticketForEmployee, setTicketForEmployee] = useState<{ firstName: string; lastName: string; employeeCode: string } | null>(null);

  // Onboarding bundle items (for ONBOARDING_BUNDLE ticket type)
  const [bundleItems, setBundleItems]       = useState<{ category: string; specs: string }[]>([{ category: "LAPTOP", specs: "" }]);
  const [bundleStartDate, setBundleStartDate] = useState("");
  const [bundleLocation, setBundleLocation] = useState("");

  // Onboarding queue (HRMS-0071)
  const [onboardingEmps, setOnboardingEmps]     = useState<EmployeeHit[]>([]);
  const [bundleTickets, setBundleTickets2]       = useState<ITTicket[]>([]);
  const [onboardingLoading, setOnboardingLoading] = useState(false);

  // Expiring assets (HRMS-0073)
  const [expiringData, setExpiringData]         = useState<ExpiringData | null>(null);

  // Expand ticket for comments
  const [expandedTicket, setExpandedTicket]   = useState<string | null>(null);
  const [ticketDetail, setTicketDetail]       = useState<(ITTicket & { comments: TicketComment[] }) | null>(null);
  const [newComment, setNewComment]           = useState("");
  const [ticketActionForm, setTicketActionForm] = useState({ status: "", assignToMe: false, resolutionNotes: "", fulfillAssetId: "" });
  const [fulfillAssetSearchVal, setFulfillAssetSearchVal] = useState("");
  const [fulfillAssetHits, setFulfillAssetHits] = useState<{ id: string; assetTag: string; name: string }[]>([]);
  const fulfillAssetRef = useRef<ReturnType<typeof setTimeout>>();

  // Verification campaigns
  const [campaigns, setCampaigns]           = useState<Campaign[]>([]);
  const [campaignDetail, setCampaignDetail] = useState<Campaign | null>(null);
  const [exceptions, setExceptions]         = useState<VerificationTask[]>([]);
  const [myVerifyTasks, setMyVerifyTasks]   = useState<VerificationTask[]>([]);
  const [showCampaignForm, setShowCampaignForm] = useState(false);
  const [campaignForm, setCampaignForm]     = useState({ title: "", description: "", officeId: "" });
  const [respondForm, setRespondForm]       = useState<Record<string, { status: string; notes: string }>>({});
  const [showExtraForm, setShowExtraForm]   = useState(false);
  const [extraDescription, setExtraDescription] = useState("");
  const [resolveForm, setResolveForm]       = useState<Record<string, { resolutionNotes: string; raiseTicket: boolean; ticketPriority: string }>>({});

  // ── Loaders ────────────────────────────────────────────────────────────────

  const loadMyAssets = useCallback(async () => {
    if (!myEmpId) return;
    setLoading(true);
    try {
      const { data } = await api.get<MyAsset[]>(`/employees/${myEmpId}/assets`);
      setMyAssets(data);
    } catch (err) { logError("AssetsPage.loadMyAssets", err); } finally { setLoading(false); }
  }, [myEmpId]);

  const loadMyVerifyTasks = useCallback(async () => {
    if (!myEmpId) return;
    try {
      const { data } = await api.get<VerificationTask[]>(`/employees/${myEmpId}/verification-tasks`);
      setMyVerifyTasks(data);
    } catch (err) { logError("AssetsPage.loadMyVerifyTasks", err); }
  }, [myEmpId]);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<Campaign[]>("/asset-verification-campaigns");
      setCampaigns(data);
    } catch (err) { logError("AssetsPage.loadCampaigns", err); } finally { setLoading(false); }
  }, []);

  async function loadCampaignDetail(id: string) {
    try {
      const [detailRes, excRes] = await Promise.all([
        api.get<Campaign>(`/asset-verification-campaigns/${id}`),
        api.get<VerificationTask[]>(`/asset-verification-campaigns/${id}/exceptions`),
      ]);
      setCampaignDetail(detailRes.data);
      setExceptions(excRes.data);
    } catch (err) { logError("AssetsPage.loadCampaignDetail", err); }
  }

  const loadAssets = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filterStatus)   p.set("status",   filterStatus);
      if (filterCategory) p.set("category", filterCategory);
      if (filterOffice)   p.set("officeId", filterOffice);
      if (search)         p.set("search",   search);
      const { data } = await api.get<{ data: Asset[] }>(`/assets?${p}`);
      setAssets(data.data);
    } catch (err) { logError("AssetsPage.loadAssets", err); } finally { setLoading(false); }
  }, [filterStatus, filterCategory, filterOffice, search]);

  const loadStockSummary = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (filterOffice) p.set("officeId", filterOffice);
      const { data } = await api.get<StockSummaryRow[]>(`/assets/stock-summary?${p}`);
      setStockSummary(data);
    } catch (err) { logError("AssetsPage.loadStockSummary", err); }
  }, [filterOffice]);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (tStatus)   p.set("status",     tStatus);
      if (tPriority) p.set("priority",   tPriority);
      if (tType)     p.set("ticketType", tType);
      const { data } = await api.get<{ data: ITTicket[] }>(`/it-tickets?${p}`);
      setTickets(data.data);
    } catch (err) { logError("AssetsPage.loadTickets", err); } finally { setLoading(false); }
  }, [tStatus, tPriority, tType]);

  const loadStockRequests = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (srFilterStatus) p.set("status",   srFilterStatus);
      if (srFilterOffice) p.set("officeId", srFilterOffice);
      const { data } = await api.get<{ data: StockRequest[] }>(`/stock-requests?${p}`);
      setStockRequests(data.data);
    } catch (err) { logError("AssetsPage.loadStockRequests", err); } finally { setLoading(false); }
  }, [srFilterStatus, srFilterOffice]);

  function downloadCsv(path: string, filename: string) {
    fetch(`/api${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
      })
      .catch((err) => { logError("AssetsPage.downloadCsv", err); setMsg("Download failed"); });
  }

  async function loadAdminData() {
    setOnboardingLoading(true);
    try {
      const [empsRes, ticketsRes, expiringRes] = await Promise.all([
        api.get<{ data: EmployeeHit[] }>("/employees?status=ONBOARDING&limit=100"),
        api.get<{ data: ITTicket[] }>("/it-tickets?ticketType=ONBOARDING_BUNDLE&limit=100"),
        api.get<ExpiringData>("/assets/expiring?days=90"),
      ]);
      setOnboardingEmps(empsRes.data.data);
      setBundleTickets2(ticketsRes.data.data);
      setExpiringData(expiringRes.data);
    } catch (err) { logError("AssetsPage.loadAdminData", err); }
    finally { setOnboardingLoading(false); }
  }

  useEffect(() => {
    if (tab === "mine")        { loadMyAssets(); loadMyVerifyTasks(); }
    if (tab === "inventory")   { loadAssets(); loadStockSummary(); }
    if (tab === "tickets")     loadTickets();
    if (tab === "admin")       loadAdminData();
    if (tab === "procurement") loadStockRequests();
    if (tab === "verification") loadCampaigns();
    if (tab === "disposals")   loadAssets();
  }, [tab]);

  useEffect(() => { if (tab === "inventory") { loadAssets(); loadStockSummary(); } }, [filterStatus, filterCategory, filterOffice]);
  useEffect(() => { if (showManageTypes) loadTypeRows(); }, [showManageTypes]);
  useEffect(() => { if (tab === "tickets")   loadTickets(); }, [tStatus, tPriority, tType]);
  useEffect(() => { if (tab === "procurement") loadStockRequests(); }, [srFilterStatus, srFilterOffice]);

  useEffect(() => {
    if (!isIT && !isFinance) return;
    api.get<Office[]>("/assets/offices").then(r => setOffices(r.data)).catch((err) => logError("AssetsPage.loadOffices", err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep link from an employee's profile ("Raise IT Ticket" next to the onboarding checklist)
  useEffect(() => {
    if (searchParams.get("newTicket") !== "1") return;
    const employeeId = searchParams.get("employeeId") ?? "";
    const ticketType = searchParams.get("ticketType") ?? "NEW_EQUIPMENT";
    setTab("tickets");
    setTicketForm(f => ({ ...f, ticketType, employeeId }));
    setShowTicketForm(true);
    if (employeeId) {
      api.get<{ firstName: string; lastName: string; employeeCode: string }>(`/employees/${employeeId}`)
        .then(r => setTicketForEmployee(r.data))
        .catch((err) => logError("AssetsPage.loadTicketForEmployee", err, { employeeId }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep link from a notification (e.g. "needs Finance sign-off" -> /assets?tab=disposals)
  useEffect(() => {
    const requested = searchParams.get("tab");
    if (!requested) return;
    const valid: Tab[] = ["mine", "inventory", "tickets", "admin", "procurement", "verification", "import", "disposals"];
    if (valid.includes(requested as Tab)) setTab(requested as Tab);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search
  function onSearchChange(v: string) {
    setSearch(v);
    clearTimeout(searchRef.current);
    searchRef.current = setTimeout(() => loadAssets(), 300);
  }

  // Employee search for assign modal
  function onEmpSearch(v: string) {
    setEmpSearch(v);
    clearTimeout(empRef.current);
    if (!v.trim()) { setEmpHits([]); return; }
    empRef.current = setTimeout(async () => {
      setEmpLoading(true);
      try {
        const { data } = await api.get<{ data: EmployeeHit[] }>(`/employees?search=${encodeURIComponent(v)}&limit=8`);
        setEmpHits(data.data);
      } catch (err) { logError("AssetsPage.empSearch", err); } finally { setEmpLoading(false); }
    }, 250);
  }

  // Asset search for ticket link
  function onAssetSearch(v: string) {
    setAssetSearchVal(v);
    if (!v.trim()) { setAssetHits([]); return; }
    clearTimeout(searchRef.current);
    searchRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get<{ data: Asset[] }>(`/assets?search=${encodeURIComponent(v)}&limit=8`);
        setAssetHits(data.data.map(a => ({ id: a.id, assetTag: a.assetTag, name: a.name })));
      } catch (err) { logError("AssetsPage.onAssetSearch", err); }
    }, 250);
  }

  // Asset search for resolving a ticket by fulfilling it -- only AVAILABLE assets
  function onFulfillAssetSearch(v: string) {
    setFulfillAssetSearchVal(v);
    if (!v.trim()) { setFulfillAssetHits([]); return; }
    clearTimeout(fulfillAssetRef.current);
    fulfillAssetRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get<{ data: Asset[] }>(`/assets?search=${encodeURIComponent(v)}&status=AVAILABLE&limit=8`);
        setFulfillAssetHits(data.data.map(a => ({ id: a.id, assetTag: a.assetTag, name: a.name })));
      } catch (err) { logError("AssetsPage.onFulfillAssetSearch", err); }
    }, 250);
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  async function loadTypeRows() {
    try {
      const { data } = await api.get("/admin/lookup?category=ASSET_CATEGORY");
      setTypeRows(data);
    } catch (e) { logError("AssetsPage.loadTypeRows", e); }
  }

  async function addAssetType() {
    setSaving(true); setErr("");
    try {
      await api.post("/admin/lookup", {
        category: "ASSET_CATEGORY",
        value: newTypeValue.trim().replace(/\s+/g, "_"),
        displayName: newTypeLabel.trim(),
      });
      setNewTypeValue(""); setNewTypeLabel("");
      await loadTypeRows(); refreshLookup();
    } catch (e) { logError("AssetsPage.addAssetType", e); setErr(errMsg(e) ?? "Failed to add type"); }
    setSaving(false);
  }

  async function toggleAssetTypeActive(row: { id: string; isActive: boolean }) {
    setSaving(true);
    try {
      await api.patch(`/admin/lookup/${row.id}`, { isActive: !row.isActive });
      await loadTypeRows(); refreshLookup();
    } catch (e) { logError("AssetsPage.toggleAssetTypeActive", e); setErr(errMsg(e) ?? "Failed to update"); }
    setSaving(false);
  }

  async function deleteAssetType(id: string) {
    setSaving(true);
    try {
      await api.delete(`/admin/lookup/${id}`);
      await loadTypeRows(); refreshLookup();
    } catch (e) { logError("AssetsPage.deleteAssetType", e); setErr(errMsg(e) ?? "Failed to delete"); }
    setSaving(false);
  }

  async function createAsset() {
    if (!assetForm.officeId) { setErr("Office is required"); throw new Error("validation"); }
    const invalidNumbers: string[] = [];
    if (assetForm.purchasePrice && parseFloat(assetForm.purchasePrice) <= 0) invalidNumbers.push("Purchase Price");
    if (assetForm.rentalMonthlyCost && parseFloat(assetForm.rentalMonthlyCost) <= 0) invalidNumbers.push("Rental Monthly Cost");
    if (assetForm.licenseSeats && parseInt(assetForm.licenseSeats, 10) <= 0) invalidNumbers.push("License Seats");
    if (assetForm.telecomMonthlyCharge && parseFloat(assetForm.telecomMonthlyCharge) <= 0) invalidNumbers.push("Telecom Monthly Charge");
    if (invalidNumbers.length) {
      setErr(`${invalidNumbers.join(", ")} must be greater than 0 — leave blank if unknown`);
      throw new Error("validation");
    }
    const hiVal = ["LAPTOP","DESKTOP","MOBILE"];
    if (hiVal.includes(assetForm.category)) {
      const missing: string[] = [];
      if (!assetForm.model)         missing.push("Model");
      if (!assetForm.serialNumber)  missing.push("Serial Number");
      if (!assetForm.purchasePrice) missing.push("Purchase Price");
      if (!assetForm.purchaseDate)  missing.push("Purchase Date");
      if (missing.length) { setErr(`Required for ${assetForm.category}: ${missing.join(", ")}`); throw new Error("validation"); }
    }
    if (assetForm.category === "SOFTWARE_LICENSE" && !assetForm.licenseType) {
      setErr("License Type is required for Software License assets"); throw new Error("validation");
    }
    if (assetForm.category === "TELECOM_SERVICE") {
      if (!assetForm.telecomCarrier) { setErr("Carrier is required for Telecom Service assets"); throw new Error("validation"); }
      if (!assetForm.telecomPhoneNumber && !assetForm.telecomSimNumber) {
        setErr("Phone Number or SIM Number is required for Telecom Service assets"); throw new Error("validation");
      }
    }
    setSaving(true); setErr("");
    try {
      const isSL      = assetForm.category === "SOFTWARE_LICENSE";
      const isTelecom = assetForm.category === "TELECOM_SERVICE";
      await api.post("/assets", {
        ...assetForm,
        officeId:      assetForm.officeId || undefined,
        purchasePrice: assetForm.purchasePrice ? parseFloat(assetForm.purchasePrice) : undefined,
        make:         (!isSL && assetForm.make)         ? assetForm.make         : undefined,
        model:        (!isSL && assetForm.model)        ? assetForm.model        : undefined,
        serialNumber: (!isSL && assetForm.serialNumber) ? assetForm.serialNumber : undefined,
        macAddress:   (!isSL && assetForm.macAddress)   ? assetForm.macAddress   : undefined,
        condition:    isSL ? undefined : assetForm.condition,
        purchaseDate: assetForm.purchaseDate || undefined,
        warrantyExpiry: assetForm.warrantyExpiry || undefined,
        vendor:       assetForm.vendor       || undefined,
        poReference:  assetForm.poReference  || undefined,
        notes:        assetForm.notes        || undefined,
        rentalProvider:    assetForm.rentalProvider    || undefined,
        rentalStartDate:   assetForm.rentalStartDate   || undefined,
        rentalEndDate:     assetForm.rentalEndDate     || undefined,
        rentalMonthlyCost: assetForm.rentalMonthlyCost ? parseFloat(assetForm.rentalMonthlyCost) : undefined,
        location:     assetForm.location || undefined,
        licenseType:         isSL ? (assetForm.licenseType         || undefined) : undefined,
        licenseSeats:        isSL && assetForm.licenseSeats ? parseInt(assetForm.licenseSeats, 10) : undefined,
        licenseKey:          isSL ? (assetForm.licenseKey          || undefined) : undefined,
        licenseRenewalOwner: isSL ? (assetForm.licenseRenewalOwner || undefined) : undefined,
        telecomCarrier:      isTelecom ? (assetForm.telecomCarrier     || undefined) : undefined,
        telecomPhoneNumber:  isTelecom ? (assetForm.telecomPhoneNumber || undefined) : undefined,
        telecomSimNumber:    isTelecom ? (assetForm.telecomSimNumber   || undefined) : undefined,
        telecomPlanName:     isTelecom ? (assetForm.telecomPlanName    || undefined) : undefined,
        telecomMonthlyCharge: isTelecom && assetForm.telecomMonthlyCharge ? parseFloat(assetForm.telecomMonthlyCharge) : undefined,
        telecomLockStatus:   isTelecom ? (assetForm.telecomLockStatus  || undefined) : undefined,
      });
      setShowAssetForm(false); setAssetForm({ ...BLANK_ASSET });
      setMsg("Asset created"); loadAssets(); loadStockSummary();
    } catch (e: unknown) {
      setErr(errMsg(e) ?? "Failed");
      throw e;
    } finally { setSaving(false); }
  }

  async function uploadAssetPhoto(assetId: string, phase: "before" | "after", file: File) {
    const r = await api.post<{ uploadUrl: string }>(`/assets/${assetId}/assignment-photo-upload-url`, {
      mimeType: file.type, phase,
    });
    const res = await fetch(r.data.uploadUrl, { method: "PUT", body: file, headers: { "Content-Type": file.type } });
    if (!res.ok) throw new Error("Upload failed");
  }

  async function doAssign() {
    if (!assigningAsset || !assignForm.employeeId) throw new Error("validation");
    setSaving(true); setErr("");
    try {
      await api.patch(`/assets/${assigningAsset.id}/assign`, {
        employeeId:         assignForm.employeeId,
        conditionAtIssue:   assignForm.conditionAtIssue || undefined,
        expectedReturnDate: assignForm.expectedReturnDate || undefined,
        notes:              assignForm.notes || undefined,
      });
      if (beforePhotoFile) await uploadAssetPhoto(assigningAsset.id, "before", beforePhotoFile);
      setAssigningAsset(null); setBeforePhotoFile(null); setMsg("Asset assigned");
      loadAssets(); loadMyAssets(); loadStockSummary();
    } catch (e: unknown) {
      setErr(errMsg(e) ?? "Failed");
      throw e;
    } finally { setSaving(false); }
  }

  async function doReturn() {
    if (!returningAsset) throw new Error("validation");
    setSaving(true); setErr("");
    try {
      if (afterPhotoFile) await uploadAssetPhoto(returningAsset.id, "after", afterPhotoFile);
      await api.patch(`/assets/${returningAsset.id}/return`, {
        conditionAtReturn: returnForm.conditionAtReturn || undefined,
        returnNotes:       returnForm.returnNotes       || undefined,
      });
      setReturningAsset(null); setAfterPhotoFile(null); setMsg("Asset returned");
      loadAssets(); loadMyAssets(); loadStockSummary();
    } catch (e: unknown) {
      setErr(errMsg(e) ?? "Failed");
      throw e;
    } finally { setSaving(false); }
  }

  async function doTransfer() {
    if (!transferringAsset || !transferForm.employeeId) throw new Error("validation");
    setSaving(true); setErr("");
    try {
      await api.patch(`/assets/${transferringAsset.id}/transfer`, {
        employeeId:         transferForm.employeeId,
        conditionAtReturn:  transferForm.conditionAtReturn || undefined,
        conditionAtIssue:   transferForm.conditionAtIssue  || undefined,
        expectedReturnDate: transferForm.expectedReturnDate || undefined,
        notes:              transferForm.notes || undefined,
      });
      setTransferringAsset(null); setMsg("Asset transferred");
      loadAssets(); loadMyAssets(); loadStockSummary();
    } catch (e: unknown) {
      setErr(errMsg(e) ?? "Failed");
      throw e;
    } finally { setSaving(false); }
  }

  async function confirmCustody(id: string) {
    setSaving(true); setErr("");
    try {
      await api.patch(`/stock-requests/${id}/confirm-custody`);
      setMsg("Custody confirmed"); loadStockRequests();
    } catch (e: unknown) {
      setErr(errMsg(e) ?? "Failed");
    } finally { setSaving(false); }
  }

  async function acknowledgeAsset(assignmentId: string) {
    setSaving(true); setErr("");
    try {
      await api.patch(`/assets/assignments/${assignmentId}/acknowledge`);
      setMsg("Receipt acknowledged"); loadMyAssets();
    } catch (e: unknown) {
      setErr(errMsg(e) ?? "Failed");
    } finally { setSaving(false); }
  }

  async function respondToTask(taskId: string) {
    const f = respondForm[taskId];
    if (!f?.status) return;
    setSaving(true); setErr("");
    try {
      await api.patch(`/asset-verification-tasks/${taskId}`, { status: f.status, notes: f.notes || undefined });
      setMsg("Response recorded"); loadMyVerifyTasks();
    } catch (e: unknown) { setErr(errMsg(e) ?? "Failed"); } finally { setSaving(false); }
  }

  async function submitExtraReport(campaignId: string) {
    if (!extraDescription.trim()) return;
    setSaving(true); setErr("");
    try {
      await api.post(`/asset-verification-campaigns/${campaignId}/extra`, { extraDescription });
      setShowExtraForm(false); setExtraDescription(""); setMsg("Reported"); loadMyVerifyTasks();
    } catch (e: unknown) { setErr(errMsg(e) ?? "Failed"); } finally { setSaving(false); }
  }

  async function startCampaign() {
    setSaving(true); setErr("");
    try {
      await api.post("/asset-verification-campaigns", {
        title: campaignForm.title,
        description: campaignForm.description || undefined,
        officeId: isRegionalScoped ? undefined : (campaignForm.officeId || null),
      });
      setShowCampaignForm(false); setCampaignForm({ title: "", description: "", officeId: "" });
      setMsg("Campaign started"); loadCampaigns();
    } catch (e: unknown) { setErr(errMsg(e) ?? "Failed"); throw e; } finally { setSaving(false); }
  }

  async function closeCampaign(id: string) {
    setSaving(true); setErr("");
    try {
      await api.patch(`/asset-verification-campaigns/${id}/close`);
      setMsg("Campaign closed"); loadCampaigns(); loadCampaignDetail(id);
    } catch (e: unknown) { setErr(errMsg(e) ?? "Failed"); throw e; } finally { setSaving(false); }
  }

  async function resolveException(taskId: string, campaignId: string) {
    const f = resolveForm[taskId];
    if (!f?.resolutionNotes.trim()) return;
    setSaving(true); setErr("");
    try {
      await api.patch(`/asset-verification-tasks/${taskId}/resolve`, f);
      setMsg("Exception resolved"); loadCampaignDetail(campaignId);
    } catch (e: unknown) { setErr(errMsg(e) ?? "Failed"); } finally { setSaving(false); }
  }

  async function toggleAssetHistory(assetId: string) {
    if (expandedAssetId === assetId) { setExpandedAssetId(null); return; }
    setExpandedAssetId(assetId); setLoadingHistory(true);
    try {
      const r = await api.get<Omit<Asset, "assignments" | "statusHistory"> & { assignments: AssignmentHistoryItem[]; statusHistory: StatusHistoryItem[]; maintenanceRecords: MaintenanceRecordItem[] }>(`/assets/${assetId}`);
      setAssetHistory(r.data.assignments);
      setAssetStatusHistory(r.data.statusHistory);
      setAssetMaintenance(r.data.maintenanceRecords);
    } catch (e: unknown) {
      setErr(errMsg(e) ?? "Failed to load history");
    } finally { setLoadingHistory(false); }
  }

  async function refreshAssetHistory(assetId: string) {
    const r = await api.get<Omit<Asset, "assignments" | "statusHistory"> & { assignments: AssignmentHistoryItem[]; statusHistory: StatusHistoryItem[]; maintenanceRecords: MaintenanceRecordItem[] }>(`/assets/${assetId}`);
    setAssetHistory(r.data.assignments);
    setAssetStatusHistory(r.data.statusHistory);
    setAssetMaintenance(r.data.maintenanceRecords);
  }

  async function createMaintenance(assetId: string) {
    try {
      await api.post(`/assets/${assetId}/maintenance`, {
        vendor: maintenanceForm.vendor || undefined,
        cost: maintenanceForm.cost ? Number(maintenanceForm.cost) : undefined,
        currency: maintenanceForm.currency,
        faultDescription: maintenanceForm.faultDescription,
        sentOutAt: maintenanceForm.sentOutAt || undefined,
        notes: maintenanceForm.notes || undefined,
      });
      setMsg("Maintenance record logged");
      setShowMaintenanceForm(false);
      setMaintenanceForm({ vendor: "", cost: "", currency: "INR", faultDescription: "", sentOutAt: "", notes: "" });
      await refreshAssetHistory(assetId);
    } catch (e: unknown) {
      logError("AssetsPage.createMaintenance", e);
      setMsg(errMsg(e) ?? "Failed to log maintenance record");
      throw e;
    }
  }

  async function markMaintenanceReturned(assetId: string, recordId: string) {
    try {
      await api.patch(`/assets/${assetId}/maintenance/${recordId}`, { returnedAt: new Date().toISOString().slice(0, 10) });
      setMsg("Marked as returned");
      await refreshAssetHistory(assetId);
    } catch (e: unknown) {
      logError("AssetsPage.markMaintenanceReturned", e);
      setMsg(errMsg(e) ?? "Failed to update maintenance record");
    }
  }

  async function viewAssetPhoto(assignmentId: string, phase: "before" | "after") {
    try {
      const r = await api.get<{ url: string }>(`/assets/assignments/${assignmentId}/photo-url?phase=${phase}`);
      window.open(r.data.url, "_blank");
    } catch (e: unknown) {
      setErr(errMsg(e) ?? "Failed to load photo");
    }
  }

  async function doStatusChange(assetId: string, status: string) {
    try {
      await api.patch(`/assets/${assetId}/status`, { status });
      setMsg(`Status → ${status}`); loadAssets(); loadStockSummary();
    } catch (e: unknown) {
      setMsg(errMsg(e) ?? "Failed");
    }
  }

  async function submitStockRequest() {
    setSaving(true); setErr("");
    try {
      const payload = {
        officeId:          stockRequestForm.officeId || undefined,
        category:          stockRequestForm.category,
        model:             stockRequestForm.model || undefined,
        quantity:          parseInt(stockRequestForm.quantity, 10),
        estimatedUnitCost: parseFloat(stockRequestForm.estimatedUnitCost),
        currency:          stockRequestForm.currency,
        justification:     stockRequestForm.justification,
        linkedTicketId:    stockRequestForm.linkedTicketId || undefined,
      };
      if (editingStockRequestId) {
        await api.patch(`/stock-requests/${editingStockRequestId}`, payload);
      } else {
        await api.post("/stock-requests", payload);
      }
      setShowStockRequestForm(false); setStockRequestForm({ ...BLANK_STOCK_REQUEST }); setEditingStockRequestId(null);
      setMsg(editingStockRequestId ? "Stock request resubmitted" : "Stock request submitted"); loadStockRequests();
    } catch (e: unknown) {
      setErr(errMsg(e) ?? "Failed");
      throw e;
    } finally { setSaving(false); }
  }

  function startEditStockRequest(sr: StockRequest) {
    setEditingStockRequestId(sr.id);
    setStockRequestForm({
      officeId: sr.office.id, category: sr.category, model: sr.model ?? "",
      quantity: String(sr.quantity), estimatedUnitCost: sr.estimatedUnitCost,
      currency: sr.currency, justification: sr.justification,
      linkedTicketId: sr.linkedTicket?.id ?? "",
    });
    setShowStockRequestForm(true);
  }

  async function cancelStockRequest(id: string) {
    try {
      await api.patch(`/stock-requests/${id}/cancel`, {});
      setMsg("Stock request cancelled"); loadStockRequests();
    } catch (e: unknown) {
      setMsg(errMsg(e) ?? "Failed");
    }
  }

  async function financeApprove(id: string) {
    setSaving(true);
    try {
      await api.patch(`/stock-requests/${id}/finance-approve`, { remarks: financeRemarksDraft[id] || undefined });
      setFinanceActionTarget(null); setFinanceRemarksDraft(f => ({ ...f, [id]: "" }));
      setMsg("Stock request approved"); loadStockRequests();
    } catch (e: unknown) {
      setErr(errMsg(e) ?? "Failed");
      throw e;
    } finally { setSaving(false); }
  }

  async function financeReject(id: string) {
    const remarks = financeRemarksDraft[id] || "";
    if (!remarks.trim()) throw new Error("validation");
    setSaving(true);
    try {
      await api.patch(`/stock-requests/${id}/finance-reject`, { remarks });
      setFinanceActionTarget(null); setFinanceRemarksDraft(f => ({ ...f, [id]: "" }));
      setMsg("Stock request rejected"); loadStockRequests();
    } catch (e: unknown) {
      setErr(errMsg(e) ?? "Failed");
      throw e;
    } finally { setSaving(false); }
  }

  async function revokeCustody(id: string) {
    const reason = financeRemarksDraft[id] || "";
    if (!reason.trim()) throw new Error("validation");
    setSaving(true);
    try {
      await api.patch(`/stock-requests/${id}/revoke-custody`, { reason });
      setFinanceActionTarget(null); setFinanceRemarksDraft(f => ({ ...f, [id]: "" }));
      setMsg("Custody revoked"); loadStockRequests();
    } catch (e: unknown) {
      setErr(errMsg(e) ?? "Failed");
      throw e;
    } finally { setSaving(false); }
  }

  async function uploadInvoice(sr: StockRequest) {
    if (!invoiceFile) throw new Error("validation");
    setUploadingInvoice(true); setErr(""); setVerificationResult(null); setAiUnavailable(false);
    try {
      const uploadRes = await api.post<{ uploadUrl: string }>(`/stock-requests/${sr.id}/invoice-upload-url`, { mimeType: invoiceFile.type });
      const res = await fetch(uploadRes.data.uploadUrl, { method: "PUT", body: invoiceFile, headers: { "Content-Type": invoiceFile.type } });
      if (!res.ok) throw new Error("Upload failed");
      setInvoiceFile(null);
      await loadStockRequests();
      setMsg("Invoice uploaded");
      await verifyInvoice(sr.id);
    } catch (e: unknown) {
      setErr(errMsg(e) ?? "Upload failed");
      throw e;
    } finally { setUploadingInvoice(false); }
  }

  async function uploadPo(sr: StockRequest) {
    if (!poFile) throw new Error("validation");
    setUploadingPo(true); setErr("");
    try {
      const uploadRes = await api.post<{ uploadUrl: string }>(`/stock-requests/${sr.id}/po-upload-url`, { mimeType: poFile.type });
      const res = await fetch(uploadRes.data.uploadUrl, { method: "PUT", body: poFile, headers: { "Content-Type": poFile.type } });
      if (!res.ok) throw new Error("Upload failed");
      setPoFile(null);
      await loadStockRequests();
      setMsg("Purchase order uploaded");
      if (sr.invoiceS3Key) await verifyInvoice(sr.id);
    } catch (e: unknown) {
      setErr(errMsg(e) ?? "Upload failed");
      throw e;
    } finally { setUploadingPo(false); }
  }

  async function viewPo(id: string) {
    try {
      const r = await api.get<{ url: string }>(`/stock-requests/${id}/po-url`);
      window.open(r.data.url, "_blank");
    } catch (e: unknown) {
      setMsg(errMsg(e) ?? "Failed to load PO");
    }
  }

  async function verifyInvoice(id: string) {
    setVerifying(true); setAiUnavailable(false);
    try {
      const { data } = await api.post<VerificationResult>(`/stock-requests/${id}/verify-invoice`, {
        vendor:      receiveForm.vendor || undefined,
        poReference: receiveForm.poReference || undefined,
      });
      setVerificationResult(data);
    } catch (e: unknown) {
      if (errStatus(e) === 503) { setAiUnavailable(true); setVerificationResult(null); }
      else { setErr(errMsg(e) ?? "Verification failed"); }
    } finally { setVerifying(false); }
  }

  async function confirmArrival(sr: StockRequest) {
    if (!receiveForm.assetTagPrefix) throw new Error("validation");
    setSaving(true); setErr("");
    try {
      const { data } = await api.patch<{ createdCount: number }>(`/stock-requests/${sr.id}/receive`, {
        vendor:         receiveForm.vendor || undefined,
        poReference:    receiveForm.poReference || undefined,
        assetTagPrefix: receiveForm.assetTagPrefix,
      });
      setReceivingRequestId(null); setReceiveForm({ vendor: "", poReference: "", assetTagPrefix: "" });
      setVerificationResult(null); setAiUnavailable(false);
      setMsg(`Received — ${data.createdCount} asset(s) added to inventory`); loadStockRequests();
    } catch (e: unknown) {
      setErr(errMsg(e) ?? "Failed");
      throw e;
    } finally { setSaving(false); }
  }

  async function viewInvoice(id: string) {
    try {
      const r = await api.get<{ url: string }>(`/stock-requests/${id}/invoice-url`);
      window.open(r.data.url, "_blank");
    } catch (e: unknown) {
      setMsg(errMsg(e) ?? "Failed to load invoice");
    }
  }

  function jumpToReceivedAssets(sr: StockRequest) {
    if (!sr.assetTagPrefix) return;
    setSearch(sr.assetTagPrefix);
    setTab("inventory");
  }

  async function createTicket() {
    setSaving(true); setErr("");
    try {
      const isBundle = ticketForm.ticketType === "ONBOARDING_BUNDLE";
      const typeData = isBundle
        ? { requestedAssets: bundleItems.filter(i => i.category), startDate: bundleStartDate || null, officeLocation: bundleLocation || null }
        : Object.keys(ticketForm.typeData).length ? ticketForm.typeData : undefined;
      await api.post("/it-tickets", {
        title:         ticketForm.title,
        ticketType:    ticketForm.ticketType,
        category:      ticketForm.category,
        priority:      ticketForm.priority,
        description:   ticketForm.description || undefined,
        linkedAssetId: (!isBundle && ticketForm.linkedAssetId) ? ticketForm.linkedAssetId : undefined,
        employeeId:    ticketForm.employeeId || undefined,
        typeData,
      });
      setShowTicketForm(false); setTicketForm({ ...BLANK_TICKET }); setAssetSearchVal(""); setAssetHits([]); setTicketForEmployee(null);
      setBundleItems([{ category: "LAPTOP", specs: "" }]); setBundleStartDate(""); setBundleLocation("");
      setMsg("Ticket created"); loadTickets();
    } catch (e: unknown) {
      setErr(errMsg(e) ?? "Failed");
      throw e;
    } finally { setSaving(false); }
  }

  async function loadTicketDetail(id: string) {
    try {
      const { data } = await api.get<ITTicket & { comments: TicketComment[] }>(`/it-tickets/${id}`);
      setTicketDetail(data);
      setTicketActionForm({ status: data.status, assignToMe: false, resolutionNotes: data.resolutionNotes ?? "", fulfillAssetId: "" });
      setFulfillAssetSearchVal(""); setFulfillAssetHits([]);
    } catch (err) { logError("AssetsPage.loadTicketDetail", err, { ticketId: id }); }
  }

  async function addComment() {
    if (!expandedTicket || !newComment.trim()) throw new Error("validation");
    try {
      await api.post(`/it-tickets/${expandedTicket}/comments`, { body: newComment.trim() });
      setNewComment(""); loadTicketDetail(expandedTicket);
    } catch (err) { logError("AssetsPage.addComment", err, { ticketId: expandedTicket }); throw err; }
  }

  async function updateTicketStatus() {
    if (!expandedTicket) throw new Error("validation");
    const body: Record<string, unknown> = {};
    if (ticketActionForm.status)          body["status"]          = ticketActionForm.status;
    if (ticketActionForm.assignToMe)      body["assignedToId"]    = myEmpId;
    if (ticketActionForm.resolutionNotes) body["resolutionNotes"] = ticketActionForm.resolutionNotes;
    if (ticketActionForm.fulfillAssetId)  body["fulfillAssetId"]  = ticketActionForm.fulfillAssetId;
    try {
      await api.patch(`/it-tickets/${expandedTicket}`, body);
      setMsg(ticketActionForm.fulfillAssetId ? "Ticket updated and asset assigned" : "Ticket updated");
      loadTickets(); loadTicketDetail(expandedTicket);
    } catch (e: unknown) {
      logError("AssetsPage.updateTicketStatus", e);
      setMsg(errMsg(e) ?? "Failed");
      throw e;
    }
  }

  // ── Render helpers ─────────────────────────────────────────────────────────

  function lifecycleButtons(a: Asset) {
    const btns: React.ReactNode[] = [];
    const s = a.status;
    if (s === "AVAILABLE") btns.push(
      <button key="assign" onClick={() => { setAssigningAsset(a); setAssignForm({ employeeId: "", conditionAtIssue: "GOOD", expectedReturnDate: "", notes: "" }); setEmpSearch(""); setEmpHits([]); setErr(""); }}
        style={btnStyle("var(--color-info-bg)","var(--color-info-fg)")}>Assign</button>
    );
    if (s === "ASSIGNED") btns.push(
      <button key="ret" onClick={() => { setReturningAsset(a); setReturnForm({ conditionAtReturn: "GOOD", returnNotes: "" }); setErr(""); }}
        style={btnStyle("var(--color-success-bg)","var(--color-success-fg)")}>Return</button>
    );
    if (s === "ASSIGNED") btns.push(
      <button key="transfer" onClick={() => { setTransferringAsset(a); setTransferForm({ employeeId: "", conditionAtReturn: "GOOD", conditionAtIssue: "GOOD", expectedReturnDate: "", notes: "" }); setEmpSearch(""); setEmpHits([]); setErr(""); }}
        style={btnStyle("var(--color-info-bg)","var(--color-info-fg)")}>Transfer</button>
    );
    if (["RECEIVED","IN_REPAIR","RETURNED","IN_FINANCE_CUSTODY"].includes(s)) btns.push(
      <button key="avail" onClick={() => doStatusChange(a.id, "AVAILABLE")}
        style={btnStyle("var(--color-success-bg)","var(--color-success-fg)")}>Mark Available</button>
    );
    if (s === "AVAILABLE") btns.push(
      <button key="repair" onClick={() => doStatusChange(a.id, "IN_REPAIR")}
        style={btnStyle("var(--color-warning-bg)","var(--color-warning-fg)")}>In Repair</button>
    );
    if (s === "ASSIGNED") btns.push(
      <button key="lost" onClick={() => { if (confirm("Mark as LOST?")) doStatusChange(a.id, "LOST"); }}
        style={btnStyle("var(--color-error-bg)","var(--color-error-fg)")}>Lost</button>
    );
    if (isIT && ["RETIRED","LOST","STOLEN"].includes(s)) btns.push(
      <button key="disp" onClick={() => { setDisposalPrefillAssetId(a.id); setTab("disposals"); }}
        style={btnStyle("var(--color-error-bg)","var(--color-error-fg)")}>Request Disposal</button>
    );
    return <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{btns}</div>;
  }

  // ── JSX ────────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: "var(--space-6) var(--space-8)", maxWidth: 1280 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Assets & IT</h1>
        <div style={{ display: "flex", gap: 8 }}>
          {msg && <span style={{ fontSize: 13, color: msg.toLowerCase().includes("fail") || msg.toLowerCase().includes("error") ? "var(--color-error)" : "var(--color-success)" }}>{msg}</span>}
          {tab === "inventory" && isIT && !showAssetForm && (<>
            <button onClick={() => { setShowAssetForm(true); setErr(""); }} style={btnStyle()}>+ New Asset</button>
          </>)}
          {tab === "tickets" && (
            <button onClick={() => { setShowTicketForm(true); setErr(""); setTicketForEmployee(null); }} style={btnStyle()}>+ Raise Ticket</button>
          )}
          {tab === "procurement" && isIT && !showStockRequestForm && (
            <>
              <button onClick={() => { setShowStockRequestForm(true); setEditingStockRequestId(null); setStockRequestForm({ ...BLANK_STOCK_REQUEST, officeId: isRegionalScoped ? myOfficeId : "" }); setErr(""); }} style={btnStyle()}>+ New Stock Request</button>
              <button onClick={() => navigate("/reports?tab=Finance")} style={btnStyle("transparent", "var(--color-brand-600)")}>Procurement analytics →</button>
            </>
          )}
          {tab === "verification" && isIT && !showCampaignForm && !campaignDetail && (
            <button onClick={() => { setShowCampaignForm(true); setCampaignForm({ title: "", description: "", officeId: isRegionalScoped ? myOfficeId : "" }); setErr(""); }} style={btnStyle()}>+ Start Campaign</button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: "1px solid var(--color-surface-200)" }}>
        {([
          { key: "mine",      label: "My Assets" },
          ...(isIT ? [{ key: "inventory", label: "Inventory" }] : []),
          { key: "tickets",   label: "IT Tickets" },
          ...(isIT || isFinance ? [{ key: "procurement", label: "Procurement" }] : []),
          ...(isIT ? [{ key: "verification", label: "Verification" }] : []),
          ...(isIT || isFinance ? [{ key: "disposals", label: "Disposals" }] : []),
          ...(isIT ? [{ key: "import", label: "Import" }] : []),
          ...(isIT ? [{ key: "admin", label: "IT Admin" }] : []),
        ] as { key: Tab; label: string }[]).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: "10px 20px", background: "none", border: "none", cursor: "pointer",
            fontWeight: 600, fontSize: 14,
            color: tab === t.key ? "var(--color-brand-600)" : "var(--color-text-500)",
            borderBottom: tab === t.key ? "2px solid var(--color-brand-600)" : "2px solid transparent",
            marginBottom: -1,
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── Tab: My Assets ── */}
      {tab === "mine" && myVerifyTasks.length > 0 && (
        <div style={{
          display: "flex", flexDirection: "column", gap: 10,
          padding: "14px 16px", marginBottom: 16,
          background: "var(--color-warning-bg)", border: "1px solid var(--color-warning-border)",
          borderRadius: "var(--radius-lg)", fontSize: 13,
        }}>
          <strong style={{ color: "var(--color-warning-fg-strong)" }}>
            Action required — {myVerifyTasks[0]?.campaign?.title}: please verify {myVerifyTasks.length} asset(s).
          </strong>
          {err && <p style={{ color: "var(--color-error)", fontSize: 13, marginBottom: 0 }}>{err}</p>}
          {myVerifyTasks.map(t => (
            <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", background: "var(--color-surface-0)", borderRadius: "var(--radius-md)", padding: 10 }}>
              <span style={{ fontWeight: 600, minWidth: 200 }}>{t.assignment?.asset.assetTag} — {t.assignment?.asset.name}</span>
              <DropdownSelect
                value={respondForm[t.id]?.status ?? ""}
                onChange={v => setRespondForm(f => ({ ...f, [t.id]: { status: v, notes: f[t.id]?.notes ?? "" } }))}
                options={VERIFICATION_STATUS_OPTIONS}
                placeholder="Select…"
                ariaLabel="Verification result"
                className="w-[140px] justify-between"
                open={openFilter === `respond-${t.id}`}
                onToggle={() => setOpenFilter(f => (f === `respond-${t.id}` ? null : `respond-${t.id}`))}
                onClose={() => setOpenFilter(f => (f === `respond-${t.id}` ? null : f))}
              />
              <input style={{ ...inputStyle, flex: 1, minWidth: 160 }} placeholder="Notes (required unless Verified)"
                value={respondForm[t.id]?.notes ?? ""} onChange={e => setRespondForm(f => ({ ...f, [t.id]: { status: f[t.id]?.status ?? "", notes: e.target.value } }))} />
              <button disabled={saving || !respondForm[t.id]?.status} onClick={() => respondToTask(t.id)} style={{ ...btnStyle(), opacity: saving ? 0.7 : 1 }}>Submit</button>
            </div>
          ))}
          {!showExtraForm
            ? <button onClick={() => setShowExtraForm(true)} style={{ ...btnStyle("transparent", "var(--color-warning-fg-strong)"), textDecoration: "underline", padding: 0, alignSelf: "flex-start" }}>Report an asset not listed here</button>
            : (
              <div style={{ display: "flex", gap: 8 }}>
                <input style={{ ...inputStyle, flex: 1 }} placeholder="Describe the asset you have" value={extraDescription} onChange={e => setExtraDescription(e.target.value)} />
                <button disabled={saving} onClick={() => submitExtraReport(myVerifyTasks[0]!.campaign!.id)} style={btnStyle()}>Report</button>
                <button onClick={() => setShowExtraForm(false)} style={btnStyle("var(--color-surface-100)", "var(--color-text-700)")}>Cancel</button>
              </div>
            )}
        </div>
      )}
      {tab === "mine" && (
        loading ? <p style={{ color: "var(--color-text-400)", fontSize: 14 }}>Loading…</p>
        : myAssets.length === 0 ? <p style={{ fontSize: 14, color: "var(--color-text-400)" }}>No assets assigned to you.</p>
        : (
          <div style={{ overflowX: "auto", background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr style={{ background: "var(--color-surface-50)", borderBottom: "1px solid var(--color-surface-200)" }}>
                {["Tag","Name","Category","Condition","Issued","Expected Return","Acknowledged",""].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "10px 14px", color: "var(--color-text-500)", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.4px" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {myAssets.map((m, i) => (
                  <tr key={m.id} style={{ borderBottom: i < myAssets.length - 1 ? "1px solid var(--color-surface-100)" : "none" }}>
                    <td style={{ padding: "10px 14px", fontFamily: "monospace", fontSize: 12 }}>{m.asset.assetTag}</td>
                    <td style={{ padding: "10px 14px", fontWeight: 600 }}>{m.asset.name}</td>
                    <td style={{ padding: "10px 14px" }}>{m.asset.category}</td>
                    <td style={{ padding: "10px 14px" }}><span style={badge(STATUS_STYLE(m.conditionAtIssue ?? "GOOD"))}>{m.conditionAtIssue ?? "—"}</span></td>
                    <td style={{ padding: "10px 14px" }}>{new Date(m.assignedAt).toLocaleDateString("en-IN")}</td>
                    <td style={{ padding: "10px 14px" }}>{m.expectedReturnDate ? new Date(m.expectedReturnDate).toLocaleDateString("en-IN") : "—"}</td>
                    <td style={{ padding: "10px 14px" }}>
                      {m.acknowledgedAt
                        ? <span style={badge(STATUS_STYLE("AVAILABLE"))}>Acknowledged {new Date(m.acknowledgedAt).toLocaleDateString("en-IN")}</span>
                        : <button onClick={() => acknowledgeAsset(m.id)} disabled={saving} style={{ ...btnStyle(), opacity: saving ? 0.7 : 1 }}>Acknowledge Receipt</button>}
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <button onClick={() => { setShowTicketForm(true); setTicketForm(f => ({ ...f, ticketType: "REPLACEMENT_REPAIR", linkedAssetId: m.assetId })); setTab("tickets"); setTicketForEmployee(null); }} style={btnStyle("var(--color-surface-100)","var(--color-text-700)")}>Raise Issue</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* ── Tab: Inventory ── */}
      {tab === "inventory" && (
        <>
          {showAssetForm && (
            <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: 20, marginBottom: 20 }}>
              <h3 style={{ fontWeight: 600, fontSize: 15, marginBottom: 14 }}>New Asset</h3>
              {(() => {
                const isSL = assetForm.category === "SOFTWARE_LICENSE";
                const isTelecom = assetForm.category === "TELECOM_SERVICE";
                const isHV = ["LAPTOP","DESKTOP","MOBILE"].includes(assetForm.category);
                const hasMAC = ["LAPTOP","DESKTOP","MOBILE","NETWORKING"].includes(assetForm.category);
                const hideHardwareFields = isSL || isTelecom;
                return (<>
                  <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                    <div style={{ flex: 1, maxWidth: 280 }}>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Category *</label>
                      <DropdownSelect
                        value={assetForm.category}
                        onChange={v => setAssetForm(f => ({ ...BLANK_ASSET, officeId: f.officeId, category: v }))}
                        options={categoryNameOptions}
                        placeholder="Select category…"
                        ariaLabel="Category"
                        clearable={false}
                        className="w-full justify-between"
                        open={openFilter === "assetCategory"}
                        onToggle={() => setOpenFilter(f => (f === "assetCategory" ? null : "assetCategory"))}
                        onClose={() => setOpenFilter(f => (f === "assetCategory" ? null : f))}
                      />
                    </div>
                    <div style={{ flex: 1, maxWidth: 280 }}>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Office *</label>
                      <DropdownSelect
                        value={assetForm.officeId}
                        onChange={v => setAssetForm(f => ({ ...f, officeId: v }))}
                        options={officeOptions}
                        placeholder="Select office…"
                        ariaLabel="Office"
                        clearable={false}
                        className="w-full justify-between"
                        open={openFilter === "assetOffice"}
                        onToggle={() => setOpenFilter(f => (f === "assetOffice" ? null : "assetOffice"))}
                        onClose={() => setOpenFilter(f => (f === "assetOffice" ? null : f))}
                      />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 12 }}>
                    <div>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Asset Tag *</label>
                      <input style={inputStyle} value={assetForm.assetTag} placeholder="IT-LT-001" onChange={e => setAssetForm(f => ({ ...f, assetTag: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Name *</label>
                      <input style={inputStyle} value={assetForm.name} placeholder="e.g. Dell Latitude 5540" onChange={e => setAssetForm(f => ({ ...f, name: e.target.value }))} />
                    </div>
                    {!hideHardwareFields && (<div>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Make</label>
                      <input style={inputStyle} value={assetForm.make} placeholder="Dell" onChange={e => setAssetForm(f => ({ ...f, make: e.target.value }))} />
                    </div>)}
                    {!hideHardwareFields && (<div>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Model{isHV ? " *" : ""}</label>
                      <input style={inputStyle} value={assetForm.model} placeholder="Latitude 5540" onChange={e => setAssetForm(f => ({ ...f, model: e.target.value }))} />
                    </div>)}
                    {!hideHardwareFields && (<div>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Serial Number{isHV ? " *" : ""}</label>
                      <input style={inputStyle} value={assetForm.serialNumber} onChange={e => setAssetForm(f => ({ ...f, serialNumber: e.target.value }))} />
                    </div>)}
                    {hasMAC && (<div>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>MAC Address</label>
                      <input style={inputStyle} value={assetForm.macAddress} placeholder="AA:BB:CC:DD:EE:FF" onChange={e => setAssetForm(f => ({ ...f, macAddress: e.target.value }))} />
                    </div>)}
                    <div>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Purchase Date{isHV ? " *" : ""}</label>
                      <DatePicker style={inputStyle} value={assetForm.purchaseDate} onChange={v => setAssetForm(f => ({ ...f, purchaseDate: v }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Purchase Price{isHV ? " *" : ""}</label>
                      <input type="number" step="0.01" style={inputStyle} value={assetForm.purchasePrice} onChange={e => setAssetForm(f => ({ ...f, purchasePrice: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Currency</label>
                      <DropdownSelect
                        value={assetForm.currency}
                        onChange={v => setAssetForm(f => ({ ...f, currency: v }))}
                        options={CURRENCY_OPTIONS}
                        placeholder="INR"
                        ariaLabel="Currency"
                        clearable={false}
                        className="w-full justify-between"
                        open={openFilter === "assetCurrency"}
                        onToggle={() => setOpenFilter(f => (f === "assetCurrency" ? null : "assetCurrency"))}
                        onClose={() => setOpenFilter(f => (f === "assetCurrency" ? null : f))}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>{isSL ? "License Expiry" : "Warranty Expiry"}</label>
                      <DatePicker style={inputStyle} value={assetForm.warrantyExpiry} onChange={v => setAssetForm(f => ({ ...f, warrantyExpiry: v }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Vendor</label>
                      <input style={inputStyle} value={assetForm.vendor} onChange={e => setAssetForm(f => ({ ...f, vendor: e.target.value }))} />
                    </div>
                    {assetForm.category !== "NETWORKING" && (<div>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>PO / Invoice</label>
                      <input style={inputStyle} value={assetForm.poReference} onChange={e => setAssetForm(f => ({ ...f, poReference: e.target.value }))} />
                    </div>)}
                    <div>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Location</label>
                      <input style={inputStyle} value={assetForm.location} placeholder="e.g. 3rd Floor, Desk 12" onChange={e => setAssetForm(f => ({ ...f, location: e.target.value }))} />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 14 }}>
                    {!hideHardwareFields && (<div>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Condition</label>
                      <DropdownSelect
                        value={assetForm.condition}
                        onChange={v => setAssetForm(f => ({ ...f, condition: v as typeof CONDITIONS[number] }))}
                        options={CONDITION_OPTIONS}
                        placeholder="Condition"
                        ariaLabel="Condition"
                        clearable={false}
                        className="w-full justify-between"
                        open={openFilter === "assetCondition"}
                        onToggle={() => setOpenFilter(f => (f === "assetCondition" ? null : "assetCondition"))}
                        onClose={() => setOpenFilter(f => (f === "assetCondition" ? null : f))}
                      />
                    </div>)}
                    <div>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Initial Status</label>
                      <DropdownSelect
                        value={assetForm.status}
                        onChange={v => setAssetForm(f => ({ ...f, status: v as typeof ASSET_STATUSES[number] }))}
                        options={INITIAL_STATUS_OPTIONS}
                        placeholder="Initial status"
                        ariaLabel="Initial status"
                        clearable={false}
                        className="w-full justify-between"
                        open={openFilter === "assetStatus"}
                        onToggle={() => setOpenFilter(f => (f === "assetStatus" ? null : "assetStatus"))}
                        onClose={() => setOpenFilter(f => (f === "assetStatus" ? null : f))}
                      />
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Ownership</label>
                    <DropdownSelect
                      value={assetForm.ownershipType}
                      onChange={v => setAssetForm(f => ({ ...f, ownershipType: v }))}
                      options={OWNERSHIP_OPTIONS}
                      placeholder="Company-owned"
                      ariaLabel="Ownership"
                      clearable={false}
                      className="w-full justify-between"
                      open={openFilter === "assetOwnership"}
                      onToggle={() => setOpenFilter(f => (f === "assetOwnership" ? null : "assetOwnership"))}
                      onClose={() => setOpenFilter(f => (f === "assetOwnership" ? null : f))}
                    />
                  </div>
                  {assetForm.ownershipType === "RENTAL" && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 12, padding: "12px", background: "var(--color-surface-50)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-surface-200)" }}>
                      <div>
                        <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Rental Provider</label>
                        <input style={inputStyle} value={assetForm.rentalProvider} onChange={e => setAssetForm(f => ({ ...f, rentalProvider: e.target.value }))} placeholder="Provider name" />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Rental Start</label>
                        <DatePicker style={inputStyle} value={assetForm.rentalStartDate} onChange={v => setAssetForm(f => ({ ...f, rentalStartDate: v }))} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Rental End</label>
                        <DatePicker style={inputStyle} value={assetForm.rentalEndDate} onChange={v => setAssetForm(f => ({ ...f, rentalEndDate: v }))} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Monthly Cost (INR)</label>
                        <input type="number" step="0.01" style={inputStyle} value={assetForm.rentalMonthlyCost} onChange={e => setAssetForm(f => ({ ...f, rentalMonthlyCost: e.target.value }))} placeholder="0.00" />
                      </div>
                    </div>
                  )}
                  {isSL && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 12, padding: "12px", background: "var(--color-surface-50)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-surface-200)" }}>
                      <div>
                        <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>License Type *</label>
                        <input style={inputStyle} value={assetForm.licenseType} placeholder="e.g. Per-seat, Site license" onChange={e => setAssetForm(f => ({ ...f, licenseType: e.target.value }))} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Seats</label>
                        <input type="number" min="1" style={inputStyle} value={assetForm.licenseSeats} onChange={e => setAssetForm(f => ({ ...f, licenseSeats: e.target.value }))} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>License Key</label>
                        <input style={inputStyle} value={assetForm.licenseKey} onChange={e => setAssetForm(f => ({ ...f, licenseKey: e.target.value }))} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Renewal Owner</label>
                        <input style={inputStyle} value={assetForm.licenseRenewalOwner} placeholder="Name" onChange={e => setAssetForm(f => ({ ...f, licenseRenewalOwner: e.target.value }))} />
                      </div>
                    </div>
                  )}
                  {isTelecom && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 12, padding: "12px", background: "var(--color-surface-50)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-surface-200)" }}>
                      <div>
                        <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Carrier *</label>
                        <input style={inputStyle} value={assetForm.telecomCarrier} onChange={e => setAssetForm(f => ({ ...f, telecomCarrier: e.target.value }))} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Phone Number</label>
                        <input style={inputStyle} value={assetForm.telecomPhoneNumber} onChange={e => setAssetForm(f => ({ ...f, telecomPhoneNumber: e.target.value }))} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>SIM / ICCID Number</label>
                        <input style={inputStyle} value={assetForm.telecomSimNumber} onChange={e => setAssetForm(f => ({ ...f, telecomSimNumber: e.target.value }))} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Plan Name</label>
                        <input style={inputStyle} value={assetForm.telecomPlanName} onChange={e => setAssetForm(f => ({ ...f, telecomPlanName: e.target.value }))} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Monthly Charge</label>
                        <input type="number" step="0.01" style={inputStyle} value={assetForm.telecomMonthlyCharge} onChange={e => setAssetForm(f => ({ ...f, telecomMonthlyCharge: e.target.value }))} placeholder="0.00" />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Lock Status</label>
                        <input style={inputStyle} value={assetForm.telecomLockStatus} placeholder="e.g. Locked, Unlocked" onChange={e => setAssetForm(f => ({ ...f, telecomLockStatus: e.target.value }))} />
                      </div>
                    </div>
                  )}
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Notes</label>
                    <textarea rows={2} style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} value={assetForm.notes} onChange={e => setAssetForm(f => ({ ...f, notes: e.target.value }))} />
                  </div>
                </>);
              })()}
              {err && <p style={{ color: "var(--color-error)", fontSize: 13, marginBottom: 8 }}>{err}</p>}
              <div style={{ display: "flex", gap: 8 }}>
                <AsyncButton onClick={() => createAsset()} disabled={saving || !assetForm.assetTag || !assetForm.name || !assetForm.officeId}>Create Asset</AsyncButton>
                <button onClick={() => setShowAssetForm(false)} style={btnStyle("transparent","var(--color-text-600)")}>Cancel</button>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <input style={{ ...inputStyle, width: 280 }} value={search} onChange={e => onSearchChange(e.target.value)} placeholder="Search by tag, name, serial…" />
            <DropdownSelect
              value={filterStatus}
              onChange={setFilterStatus}
              options={ASSET_STATUS_OPTIONS}
              placeholder="All Statuses"
              ariaLabel="Asset status"
              open={openFilter === "invStatus"}
              onToggle={() => setOpenFilter(f => (f === "invStatus" ? null : "invStatus"))}
              onClose={() => setOpenFilter(f => (f === "invStatus" ? null : f))}
            />
            <DropdownSelect
              value={filterCategory}
              onChange={setFilterCategory}
              options={categoryOptions}
              placeholder="All Types"
              ariaLabel="Asset type"
              open={openFilter === "invType"}
              onToggle={() => setOpenFilter(f => (f === "invType" ? null : "invType"))}
              onClose={() => setOpenFilter(f => (f === "invType" ? null : f))}
            />
            {offices.length > 0 && (
              <DropdownSelect
                value={filterOffice}
                onChange={setFilterOffice}
                options={officeOptions}
                placeholder="All Offices"
                ariaLabel="Office"
                open={openFilter === "invOffice"}
                onToggle={() => setOpenFilter(f => (f === "invOffice" ? null : "invOffice"))}
                onClose={() => setOpenFilter(f => (f === "invOffice" ? null : f))}
              />
            )}
            <Button variant="outline" size="sm" onClick={() => {
              const p = new URLSearchParams();
              if (filterStatus)   p.set("status",   filterStatus);
              if (filterCategory) p.set("category", filterCategory);
              if (filterOffice)   p.set("officeId", filterOffice);
              if (search)         p.set("search",   search);
              downloadCsv(`/assets/export?${p}`, "asset-inventory.csv");
            }}>Export CSV</Button>
            {isAssetAdmin && (
              <Button variant="outline" size="sm" onClick={() => setShowManageTypes(v => !v)}>Manage Types</Button>
            )}
          </div>

          {showManageTypes && isAssetAdmin && (
            <Card className="p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-surface-900">Manage Inventory Types</h3>
                <Button variant="outline" size="sm" onClick={() => setShowManageTypes(false)}>Close</Button>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {typeRows.map(row => {
                  const Icon = getCategoryIcon(row.value);
                  return (
                    <Badge key={row.id} tone={row.isActive ? "neutral" : "warning"} className="py-1 px-2.5">
                      <Icon className="h-3.5 w-3.5" />
                      <span className={row.isActive ? "" : "line-through opacity-70"}>{row.displayName}</span>
                      <button
                        type="button"
                        className="ml-1 text-[10px] underline decoration-dotted"
                        onClick={() => toggleAssetTypeActive(row)}
                        disabled={saving}
                      >
                        {row.isActive ? "Deactivate" : "Activate"}
                      </button>
                      {!row.isSystem && (
                        <button
                          type="button"
                          className="ml-1 text-[10px] text-error underline decoration-dotted"
                          onClick={() => deleteAssetType(row.id)}
                          disabled={saving}
                        >
                          Delete
                        </button>
                      )}
                    </Badge>
                  );
                })}
              </div>
              <p className="text-xs text-surface-500 mb-2">Default types can be deactivated but not deleted, to preserve history on existing assets. Types you add can be deleted anytime.</p>
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <label className="block text-xs text-surface-500 mb-1">Code</label>
                  <Input className="w-[140px]" placeholder="e.g. BAGS" value={newTypeValue} onChange={e => setNewTypeValue(e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs text-surface-500 mb-1">Label</label>
                  <Input className="w-[180px]" placeholder="e.g. Bags" value={newTypeLabel} onChange={e => setNewTypeLabel(e.target.value)} />
                </div>
                <AsyncButton onClick={() => addAssetType()} disabled={saving || !newTypeValue.trim() || !newTypeLabel.trim()}>+ Add Type</AsyncButton>
              </div>
              {err && <p className="text-xs mt-2 text-error">{err}</p>}
            </Card>
          )}

          {/* Stock summary — available units in stock, by category, for the current office filter */}
          {stockSummary.length > 0 && (
            <ScrollSlider className="mb-4">
              {Object.entries(
                stockSummary.reduce<Record<string, number>>((acc, r) => {
                  acc[r.category] = (acc[r.category] ?? 0) + r.count;
                  return acc;
                }, {})
              ).map(([category, count]) => (
                <Card key={category} className="px-4 py-3 min-w-[140px] shrink-0">
                  <div className="text-2xl font-bold text-surface-900">{count}</div>
                  <div className="text-xs text-surface-500 font-medium">{category.replace(/_/g, " ")} available</div>
                </Card>
              ))}
            </ScrollSlider>
          )}

          {loading ? <p style={{ fontSize: 14, color: "var(--color-text-400)" }}>Loading…</p>
          : assets.length === 0 ? <p style={{ fontSize: 14, color: "var(--color-text-400)" }}>No assets found.</p>
          : (
            <div style={{ overflowX: "auto", background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead><tr style={{ background: "var(--color-surface-50)", borderBottom: "1px solid var(--color-surface-200)" }}>
                  {["Tag","Name","Owner","Office","Status","Condition","Assigned To","Warranty","Actions"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "10px 14px", color: "var(--color-text-500)", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.4px", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {assets.map((a, i) => {
                    const assignee = a.assignments[0]?.employee;
                    const CategoryIcon = getCategoryIcon(a.category);
                    return (
                      <Fragment key={a.id}>
                      <tr style={{ borderBottom: expandedAssetId === a.id ? "none" : (i < assets.length - 1 ? "1px solid var(--color-surface-100)" : "none") }}>
                        <td style={{ padding: "10px 14px", fontFamily: "monospace", fontSize: 12 }}>{a.assetTag}</td>
                        <td style={{ padding: "10px 14px" }}>
                          <div style={{ fontWeight: 600 }}>{a.name}</div>
                          {(a.make || a.model) && <div style={{ fontSize: 11, color: "var(--color-text-400)" }}>{[a.make, a.model].filter(Boolean).join(" ")}</div>}
                          <div className="flex items-center gap-1 text-[11px] text-[var(--color-text-400)]">
                            <CategoryIcon className="h-3 w-3" />
                            {assetCategories.find(c => c.value === a.category)?.displayName ?? a.category}
                          </div>
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <span style={badge(
                            a.ownershipType === "RENTAL"
                              ? { background: "var(--color-warning-bg)", color: "var(--color-warning-fg)" }
                              : a.ownershipType === "CUSTOMER_PROVIDED"
                                ? { background: "var(--color-surface-100)", color: "var(--color-text-secondary)" }
                                : a.ownershipType === "SHARED"
                                  ? { background: "var(--color-success-bg)", color: "var(--color-success-fg)" }
                                  : { background: "var(--color-info-bg)", color: "var(--color-info-fg)" }
                          )}>
                            {a.ownershipType === "CUSTOMER_PROVIDED" ? "Client" : a.ownershipType === "RENTAL" ? "Rental" : a.ownershipType === "SHARED" ? "Shared" : "Company"}
                          </span>
                        </td>
                        <td style={{ padding: "10px 14px", fontSize: 12, color: "var(--color-text-500)" }}>{a.office?.city ?? "—"}</td>
                        <td style={{ padding: "10px 14px" }}><span style={badge(STATUS_STYLE(a.status))}>{a.status.replace("_"," ")}</span></td>
                        <td style={{ padding: "10px 14px" }}><span style={badge(STATUS_STYLE(a.condition))}>{a.condition}</span></td>
                        <td style={{ padding: "10px 14px", fontSize: 12 }}>
                          {assignee ? `${assignee.firstName} ${assignee.lastName} (${assignee.employeeCode})` : "—"}
                        </td>
                        <td style={{ padding: "10px 14px", fontSize: 12 }}>
                          {a.warrantyExpiry
                            ? <span style={WARRANTY_STYLE(a.warrantyExpiry)}>{new Date(a.warrantyExpiry).toLocaleDateString("en-IN")}</span>
                            : "—"}
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {isIT && lifecycleButtons(a)}
                            <button onClick={() => toggleAssetHistory(a.id)} style={btnStyle("transparent","var(--color-text-600)")}>
                              {expandedAssetId === a.id ? "Hide History" : "History"}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedAssetId === a.id && (
                        <tr style={{ borderBottom: i < assets.length - 1 ? "1px solid var(--color-surface-100)" : "none" }}>
                          <td colSpan={9} style={{ padding: "10px 14px", background: "var(--color-surface-50)" }}>
                            {loadingHistory ? <p style={{ fontSize: 12, color: "var(--color-text-400)" }}>Loading…</p>
                            : assetHistory.length === 0 ? <p style={{ fontSize: 12, color: "var(--color-text-400)" }}>No assignment history.</p>
                            : (
                              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                <thead><tr>
                                  {["Employee","Assigned","Returned","Condition@Issue","Condition@Return","Acknowledged","Photos"].map(h => (
                                    <th key={h} style={{ textAlign: "left", padding: "4px 8px", color: "var(--color-text-500)", fontWeight: 600, fontSize: 10, textTransform: "uppercase" }}>{h}</th>
                                  ))}
                                </tr></thead>
                                <tbody>
                                  {assetHistory.map(h => (
                                    <tr key={h.id}>
                                      <td style={{ padding: "4px 8px" }}>{h.employee.firstName} {h.employee.lastName} ({h.employee.employeeCode})</td>
                                      <td style={{ padding: "4px 8px" }}>{new Date(h.assignedAt).toLocaleDateString("en-IN")}</td>
                                      <td style={{ padding: "4px 8px" }}>{h.returnedAt ? new Date(h.returnedAt).toLocaleDateString("en-IN") : "—"}</td>
                                      <td style={{ padding: "4px 8px" }}>{h.conditionAtIssue ?? "—"}</td>
                                      <td style={{ padding: "4px 8px" }}>{h.conditionAtReturn ?? "—"}</td>
                                      <td style={{ padding: "4px 8px" }}>{h.acknowledgedAt ? new Date(h.acknowledgedAt).toLocaleDateString("en-IN") : "—"}</td>
                                      <td style={{ padding: "4px 8px" }}>
                                        <div style={{ display: "flex", gap: 10 }}>
                                          {h.photoBeforeS3Key && (
                                            <button onClick={() => viewAssetPhoto(h.id, "before")} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 11, color: "var(--color-brand-600)", textDecoration: "underline" }}><Camera size={12} /> Before</button>
                                          )}
                                          {h.photoAfterS3Key && (
                                            <button onClick={() => viewAssetPhoto(h.id, "after")} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 11, color: "var(--color-brand-600)", textDecoration: "underline" }}><Camera size={12} /> After</button>
                                          )}
                                          {!h.photoBeforeS3Key && !h.photoAfterS3Key && "—"}
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                            <p style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-500)", textTransform: "uppercase", margin: "12px 0 4px" }}>Status History</p>
                            {loadingHistory ? null
                            : assetStatusHistory.length === 0 ? <p style={{ fontSize: 12, color: "var(--color-text-400)" }}>No status history.</p>
                            : (
                              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                <thead><tr>
                                  {["From → To","Changed By","When","Notes"].map(h => (
                                    <th key={h} style={{ textAlign: "left", padding: "4px 8px", color: "var(--color-text-500)", fontWeight: 600, fontSize: 10, textTransform: "uppercase" }}>{h}</th>
                                  ))}
                                </tr></thead>
                                <tbody>
                                  {assetStatusHistory.map(h => (
                                    <tr key={h.id}>
                                      <td style={{ padding: "4px 8px" }}>{h.fromStatus ?? "—"} → {h.toStatus}</td>
                                      <td style={{ padding: "4px 8px" }}>{h.changedBy.firstName} {h.changedBy.lastName} ({h.changedBy.employeeCode})</td>
                                      <td style={{ padding: "4px 8px" }}>{new Date(h.changedAt).toLocaleString("en-IN")}</td>
                                      <td style={{ padding: "4px 8px" }}>{h.notes ?? "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}

                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "12px 0 4px" }}>
                              <p style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-500)", textTransform: "uppercase", margin: 0 }}>Maintenance History</p>
                              {isIT && (
                                <Button variant="outline" size="sm" onClick={() => setShowMaintenanceForm(v => !v)}>
                                  {showMaintenanceForm ? "Cancel" : "+ Log Repair"}
                                </Button>
                              )}
                            </div>
                            {isIT && showMaintenanceForm && (
                              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 10, padding: 10, background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)" }}>
                                <input style={inputStyle} value={maintenanceForm.vendor} onChange={e => setMaintenanceForm(f => ({ ...f, vendor: e.target.value }))} placeholder="Vendor" />
                                <input style={inputStyle} type="number" value={maintenanceForm.cost} onChange={e => setMaintenanceForm(f => ({ ...f, cost: e.target.value }))} placeholder="Cost" />
                                <DropdownSelect
                                  value={maintenanceForm.currency}
                                  onChange={v => setMaintenanceForm(f => ({ ...f, currency: v }))}
                                  options={CURRENCY_OPTIONS}
                                  placeholder="INR"
                                  ariaLabel="Currency"
                                  clearable={false}
                                  className="w-full justify-between"
                                  open={openFilter === "maintCurrency"}
                                  onToggle={() => setOpenFilter(f => (f === "maintCurrency" ? null : "maintCurrency"))}
                                  onClose={() => setOpenFilter(f => (f === "maintCurrency" ? null : f))}
                                />
                                <div style={{ gridColumn: "1/-1" }}>
                                  <input style={inputStyle} value={maintenanceForm.faultDescription} onChange={e => setMaintenanceForm(f => ({ ...f, faultDescription: e.target.value }))} placeholder="Fault description *" />
                                </div>
                                <DatePicker style={inputStyle} value={maintenanceForm.sentOutAt} onChange={v => setMaintenanceForm(f => ({ ...f, sentOutAt: v }))} />
                                <input style={inputStyle} value={maintenanceForm.notes} onChange={e => setMaintenanceForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes" />
                                <AsyncButton size="sm" disabled={!maintenanceForm.faultDescription} onClick={() => createMaintenance(a.id)}>Save</AsyncButton>
                              </div>
                            )}
                            {loadingHistory ? null
                            : assetMaintenance.length === 0 ? <p style={{ fontSize: 12, color: "var(--color-text-400)" }}>No maintenance history.</p>
                            : (
                              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                <thead><tr>
                                  {["Sent Out / Returned","Vendor","Cost","Fault","Logged By",""].map(h => (
                                    <th key={h} style={{ textAlign: "left", padding: "4px 8px", color: "var(--color-text-500)", fontWeight: 600, fontSize: 10, textTransform: "uppercase" }}>{h}</th>
                                  ))}
                                </tr></thead>
                                <tbody>
                                  {assetMaintenance.map(m => (
                                    <tr key={m.id}>
                                      <td style={{ padding: "4px 8px" }}>
                                        {new Date(m.sentOutAt).toLocaleDateString("en-IN")} → {m.returnedAt ? new Date(m.returnedAt).toLocaleDateString("en-IN") : "—"}
                                      </td>
                                      <td style={{ padding: "4px 8px" }}>{m.vendor ?? "—"}</td>
                                      <td style={{ padding: "4px 8px" }}>{m.cost ? `${m.currency} ${m.cost}` : "—"}</td>
                                      <td style={{ padding: "4px 8px" }}>{m.faultDescription}</td>
                                      <td style={{ padding: "4px 8px" }}>{m.createdBy.firstName} {m.createdBy.lastName} ({m.createdBy.employeeCode})</td>
                                      <td style={{ padding: "4px 8px" }}>
                                        {isIT && !m.returnedAt && (
                                          <Button variant="outline" size="sm" onClick={() => markMaintenanceReturned(a.id, m.id)}>Mark Returned</Button>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Tab: Verification ── */}
      {tab === "verification" && isIT && (
        <>
          {showCampaignForm && (
            <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: 20, marginBottom: 20 }}>
              <h3 style={{ fontWeight: 600, fontSize: 15, marginBottom: 14 }}>Start Verification Campaign</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Title *</label>
                  <input style={inputStyle} value={campaignForm.title} onChange={e => setCampaignForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Q3 2026 Asset Sign-off" />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Office</label>
                  {isRegionalScoped ? (
                    <div style={{ ...inputStyle, background: "var(--color-surface-50)", color: "var(--color-text-500)" }}>
                      {offices.find(o => o.id === myOfficeId)?.name ?? "Your office"}
                    </div>
                  ) : (
                    <DropdownSelect
                      value={campaignForm.officeId}
                      onChange={v => setCampaignForm(f => ({ ...f, officeId: v }))}
                      options={officeOptions}
                      placeholder="All Offices"
                      ariaLabel="Office scope"
                      className="w-full justify-between"
                      open={openFilter === "campaignOffice"}
                      onToggle={() => setOpenFilter(f => (f === "campaignOffice" ? null : "campaignOffice"))}
                      onClose={() => setOpenFilter(f => (f === "campaignOffice" ? null : f))}
                    />
                  )}
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Description (optional)</label>
                <textarea rows={2} style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} value={campaignForm.description} onChange={e => setCampaignForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              {err && <p style={{ color: "var(--color-error)", fontSize: 13, marginBottom: 8 }}>{err}</p>}
              <div style={{ display: "flex", gap: 8 }}>
                <AsyncButton onClick={() => startCampaign()} disabled={saving || !campaignForm.title}>Start Campaign</AsyncButton>
                <button onClick={() => setShowCampaignForm(false)} style={btnStyle("transparent","var(--color-text-600)")}>Cancel</button>
              </div>
            </div>
          )}

          {!campaignDetail ? (
            loading ? <p style={{ color: "var(--color-text-400)", fontSize: 14 }}>Loading…</p>
            : campaigns.length === 0 ? <p style={{ fontSize: 14, color: "var(--color-text-400)" }}>No verification campaigns yet.</p>
            : (
              <div style={{ overflowX: "auto", background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead><tr style={{ background: "var(--color-surface-50)", borderBottom: "1px solid var(--color-surface-200)" }}>
                    {["Title","Office","Status","Progress","Started By","Started",""].map(h => (
                      <th key={h} style={{ textAlign: "left", padding: "10px 14px", color: "var(--color-text-500)", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.4px" }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {campaigns.map((c, i) => {
                      const counts = c.statusCounts ?? {};
                      const total = Object.values(counts).reduce((a, b) => a + b, 0);
                      return (
                        <tr key={c.id} style={{ borderBottom: i < campaigns.length - 1 ? "1px solid var(--color-surface-100)" : "none" }}>
                          <td style={{ padding: "10px 14px", fontWeight: 600 }}>{c.title}</td>
                          <td style={{ padding: "10px 14px" }}>{c.office ? `${c.office.name} — ${c.office.city}` : "All Offices"}</td>
                          <td style={{ padding: "10px 14px" }}><span style={badge(STATUS_STYLE(c.status === "ACTIVE" ? "ASSIGNED" : "AVAILABLE"))}>{c.status}</span></td>
                          <td style={{ padding: "10px 14px" }}>
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                              {total === 0 ? "—" : Object.entries(counts).map(([s, n]) => (
                                <span key={s} style={badge(STATUS_STYLE(s === "VERIFIED" ? "AVAILABLE" : s === "PENDING" ? "OPEN" : "LOST"))}>{s} {n}</span>
                              ))}
                            </div>
                          </td>
                          <td style={{ padding: "10px 14px" }}>{c.startedBy.firstName} {c.startedBy.lastName}</td>
                          <td style={{ padding: "10px 14px" }}>{new Date(c.startedAt).toLocaleDateString("en-IN")}</td>
                          <td style={{ padding: "10px 14px" }}>
                            <button onClick={() => loadCampaignDetail(c.id)} style={btnStyle("var(--color-surface-100)","var(--color-text-700)")}>View</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            <div>
              <SpecularButton onClick={() => setCampaignDetail(null)} className="mb-3.5">&larr; Back to campaigns</SpecularButton>
              <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: 20, marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10 }}>
                  <div>
                    <h3 style={{ fontWeight: 600, fontSize: 16 }}>{campaignDetail.title}</h3>
                    {campaignDetail.description && <p style={{ fontSize: 13, color: "var(--color-text-500)", marginTop: 4 }}>{campaignDetail.description}</p>}
                  </div>
                  <span style={badge(STATUS_STYLE(campaignDetail.status === "ACTIVE" ? "ASSIGNED" : "AVAILABLE"))}>{campaignDetail.status}</span>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                  {Object.entries(
                    (campaignDetail.tasks ?? []).reduce<Record<string, number>>((acc, t) => { acc[t.status] = (acc[t.status] ?? 0) + 1; return acc; }, {})
                  ).map(([s, n]) => (
                    <span key={s} style={badge(STATUS_STYLE(s === "VERIFIED" ? "AVAILABLE" : s === "PENDING" ? "OPEN" : "LOST"))}>{s} {n}</span>
                  ))}
                </div>
                {err && <p style={{ color: "var(--color-error)", fontSize: 13, marginBottom: 8 }}>{err}</p>}
                {campaignDetail.status === "ACTIVE" && (
                  <AsyncButton onClick={() => closeCampaign(campaignDetail.id)} disabled={saving} variant="destructive">Close Campaign</AsyncButton>
                )}
              </div>

              <h4 style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-500)", marginBottom: 8 }}>Exceptions ({exceptions.length})</h4>
              {exceptions.length === 0 ? <p style={{ fontSize: 14, color: "var(--color-text-400)" }}>No open exceptions.</p> : (
                <div style={{ overflowX: "auto", background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead><tr style={{ background: "var(--color-surface-50)", borderBottom: "1px solid var(--color-surface-200)" }}>
                      {["Employee","Asset","Status","Notes","Resolve"].map(h => (
                        <th key={h} style={{ textAlign: "left", padding: "10px 14px", color: "var(--color-text-500)", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.4px" }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {exceptions.map((t, i) => (
                        <tr key={t.id} style={{ borderBottom: i < exceptions.length - 1 ? "1px solid var(--color-surface-100)" : "none" }}>
                          <td style={{ padding: "10px 14px" }}>{t.employee?.firstName} {t.employee?.lastName}</td>
                          <td style={{ padding: "10px 14px" }}>{t.assignment ? `${t.assignment.asset.assetTag} — ${t.assignment.asset.name}` : (t.extraDescription ?? "—")}</td>
                          <td style={{ padding: "10px 14px" }}><span style={badge(STATUS_STYLE("LOST"))}>{t.status}</span></td>
                          <td style={{ padding: "10px 14px", maxWidth: 220 }}>{t.notes ?? t.extraDescription ?? "—"}</td>
                          <td style={{ padding: "10px 14px" }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 220 }}>
                              <textarea rows={2} style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} placeholder="Resolution notes *"
                                value={resolveForm[t.id]?.resolutionNotes ?? ""}
                                onChange={e => setResolveForm(f => ({ ...f, [t.id]: { resolutionNotes: e.target.value, raiseTicket: f[t.id]?.raiseTicket ?? false, ticketPriority: f[t.id]?.ticketPriority ?? "MEDIUM" } }))} />
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
                                  <input type="checkbox" checked={resolveForm[t.id]?.raiseTicket ?? false}
                                    onChange={e => setResolveForm(f => ({ ...f, [t.id]: { resolutionNotes: f[t.id]?.resolutionNotes ?? "", raiseTicket: e.target.checked, ticketPriority: f[t.id]?.ticketPriority ?? "MEDIUM" } }))} />
                                  Raise IT ticket
                                </label>
                                {resolveForm[t.id]?.raiseTicket && (
                                  <DropdownSelect
                                    value={resolveForm[t.id]?.ticketPriority ?? "MEDIUM"}
                                    onChange={v => setResolveForm(f => ({ ...f, [t.id]: { resolutionNotes: f[t.id]?.resolutionNotes ?? "", raiseTicket: true, ticketPriority: v } }))}
                                    options={TICKET_PRIORITY_OPTIONS}
                                    placeholder="MEDIUM"
                                    ariaLabel="Ticket priority"
                                    clearable={false}
                                    className="w-[110px] justify-between"
                                    open={openFilter === `resolve-${t.id}`}
                                    onToggle={() => setOpenFilter(f => (f === `resolve-${t.id}` ? null : `resolve-${t.id}`))}
                                    onClose={() => setOpenFilter(f => (f === `resolve-${t.id}` ? null : f))}
                                  />
                                )}
                              </div>
                              <button disabled={saving || !resolveForm[t.id]?.resolutionNotes?.trim()} onClick={() => resolveException(t.id, campaignDetail.id)} style={{ ...btnStyle(), opacity: saving ? 0.7 : 1 }}>Resolve</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Tab: Tickets ── */}
      {tab === "tickets" && (
        <>
          {showTicketForm && (
            <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: 20, marginBottom: 20 }}>
              <h3 style={{ fontWeight: 600, fontSize: 15, marginBottom: 14 }}>Raise IT Ticket</h3>
              {ticketForEmployee && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, background: "var(--color-brand-50)", border: "1px solid var(--color-brand-200)", borderRadius: "var(--radius-md)", padding: "8px 12px", fontSize: 13 }}>
                  <span style={{ color: "var(--color-text-600)" }}>Raising for:</span>
                  <strong>{ticketForEmployee.firstName} {ticketForEmployee.lastName}</strong>
                  <span style={{ fontFamily: "monospace", fontSize: 12, color: "var(--color-text-500)" }}>({ticketForEmployee.employeeCode})</span>
                  <button
                    onClick={() => { setTicketForEmployee(null); setTicketForm(f => ({ ...f, employeeId: "" })); }}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "var(--color-brand-600)", marginLeft: "auto" }}
                  >
                    Clear
                  </button>
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Title *</label>
                  <input style={inputStyle} value={ticketForm.title} onChange={e => setTicketForm(f => ({ ...f, title: e.target.value }))} placeholder="Brief summary" />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Ticket Type</label>
                  <DropdownSelect
                    value={ticketForm.ticketType}
                    onChange={v => setTicketForm(f => ({ ...f, ticketType: v }))}
                    options={TICKET_TYPE_OPTIONS}
                    placeholder="Ticket type"
                    ariaLabel="Ticket type"
                    clearable={false}
                    className="w-full justify-between"
                    open={openFilter === "ticketType"}
                    onToggle={() => setOpenFilter(f => (f === "ticketType" ? null : "ticketType"))}
                    onClose={() => setOpenFilter(f => (f === "ticketType" ? null : f))}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Priority</label>
                  <DropdownSelect
                    value={ticketForm.priority}
                    onChange={v => setTicketForm(f => ({ ...f, priority: v }))}
                    options={TICKET_PRIORITY_OPTIONS}
                    placeholder="MEDIUM"
                    ariaLabel="Priority"
                    clearable={false}
                    className="w-full justify-between"
                    open={openFilter === "ticketPriority"}
                    onToggle={() => setOpenFilter(f => (f === "ticketPriority" ? null : "ticketPriority"))}
                    onClose={() => setOpenFilter(f => (f === "ticketPriority" ? null : f))}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Category</label>
                  <input style={inputStyle} value={ticketForm.category} onChange={e => setTicketForm(f => ({ ...f, category: e.target.value }))} placeholder="LAPTOP, EMAIL, VPN…" />
                </div>

                {/* Type-specific fields */}
                {ticketForm.ticketType === "REPLACEMENT_REPAIR" && (
                  <div style={{ gridColumn: "1/-1" }}>
                    <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Link Asset (search by tag/name)</label>
                    <div style={{ position: "relative" }}>
                      <input style={inputStyle} value={assetSearchVal} onChange={e => onAssetSearch(e.target.value)} placeholder="IT-LT-001 / MacBook…" />
                      {assetHits.length > 0 && (
                        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", zIndex: 10 }}>
                          {assetHits.map(a => (
                            <div key={a.id} onClick={() => { setTicketForm(f => ({ ...f, linkedAssetId: a.id })); setAssetSearchVal(`${a.assetTag} — ${a.name}`); setAssetHits([]); }}
                              style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13 }}>
                              <strong>{a.assetTag}</strong> — {a.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Reason Code</label>
                      <DropdownSelect
                        value={ticketForm.typeData["reasonCode"] ?? ""}
                        onChange={v => setTicketForm(f => ({ ...f, typeData: { ...f.typeData, reasonCode: v } }))}
                        options={REASON_CODE_OPTIONS}
                        placeholder="Select…"
                        ariaLabel="Reason code"
                        className="w-full justify-between"
                        open={openFilter === "ticketReason"}
                        onToggle={() => setOpenFilter(f => (f === "ticketReason" ? null : "ticketReason"))}
                        onClose={() => setOpenFilter(f => (f === "ticketReason" ? null : f))}
                      />
                    </div>
                  </div>
                )}

                {ticketForm.ticketType === "NEW_EQUIPMENT" && (
                  <div style={{ gridColumn: "1/-1", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div style={{ gridColumn: "1/-1" }}>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Justification *</label>
                      <textarea rows={2} style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} value={ticketForm.typeData["justification"] ?? ""} onChange={e => setTicketForm(f => ({ ...f, typeData: { ...f.typeData, justification: e.target.value } }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Asset Category Requested</label>
                      <input style={inputStyle} value={ticketForm.typeData["assetCategory"] ?? ""} onChange={e => setTicketForm(f => ({ ...f, typeData: { ...f.typeData, assetCategory: e.target.value } }))} placeholder="LAPTOP, MONITOR…" />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Desired By</label>
                      <DatePicker style={inputStyle} value={ticketForm.typeData["desiredBy"] ?? ""} onChange={v => setTicketForm(f => ({ ...f, typeData: { ...f.typeData, desiredBy: v } }))} />
                    </div>
                  </div>
                )}

                {ticketForm.ticketType === "ACCESS_REQUEST" && (
                  <div style={{ gridColumn: "1/-1", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                    <div style={{ gridColumn: "1/-1" }}>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>System / Application *</label>
                      <input style={inputStyle} value={ticketForm.typeData["system"] ?? ""} onChange={e => setTicketForm(f => ({ ...f, typeData: { ...f.typeData, system: e.target.value } }))} placeholder="Jira, AWS, GitHub…" />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Access Level</label>
                      <DropdownSelect
                        value={ticketForm.typeData["accessLevel"] ?? "READ"}
                        onChange={v => setTicketForm(f => ({ ...f, typeData: { ...f.typeData, accessLevel: v } }))}
                        options={ACCESS_LEVEL_OPTIONS}
                        placeholder="READ"
                        ariaLabel="Access level"
                        clearable={false}
                        className="w-full justify-between"
                        open={openFilter === "ticketAccess"}
                        onToggle={() => setOpenFilter(f => (f === "ticketAccess" ? null : "ticketAccess"))}
                        onClose={() => setOpenFilter(f => (f === "ticketAccess" ? null : f))}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Expiry (optional)</label>
                      <DatePicker style={inputStyle} value={ticketForm.typeData["expiry"] ?? ""} onChange={v => setTicketForm(f => ({ ...f, typeData: { ...f.typeData, expiry: v } }))} />
                    </div>
                  </div>
                )}

                {/* Onboarding Bundle — multi-asset rows */}
                {ticketForm.ticketType === "ONBOARDING_BUNDLE" && (
                  <div style={{ gridColumn: "1/-1" }}>
                    <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 6 }}>Requested Assets *</label>
                    {bundleItems.map((item, i) => (
                      <div key={i} style={{ display: "grid", gridTemplateColumns: "160px 1fr auto", gap: 8, marginBottom: 8, alignItems: "center" }}>
                        <DropdownSelect
                          value={item.category}
                          onChange={v => setBundleItems(items => items.map((it, idx) => idx === i ? { ...it, category: v } : it))}
                          options={BUNDLE_CATEGORY_OPTIONS}
                          placeholder="LAPTOP"
                          ariaLabel="Item category"
                          clearable={false}
                          className="w-full justify-between"
                          open={openFilter === `bundle-${i}`}
                          onToggle={() => setOpenFilter(f => (f === `bundle-${i}` ? null : `bundle-${i}`))}
                          onClose={() => setOpenFilter(f => (f === `bundle-${i}` ? null : f))}
                        />
                        <input style={inputStyle} value={item.specs} onChange={e => setBundleItems(items => items.map((it, idx) => idx === i ? { ...it, specs: e.target.value } : it))} placeholder="Preferred specs / model (optional)" />
                        {bundleItems.length > 1 && (
                          <button onClick={() => setBundleItems(items => items.filter((_, idx) => idx !== i))} style={{ display: "inline-flex", alignItems: "center", padding: "6px 10px", border: "1px solid var(--color-error-border)", borderRadius: "var(--radius-md)", background: "transparent", color: "var(--color-error)", cursor: "pointer", fontSize: 13, fontWeight: 700 }}><X size={14} /></button>
                        )}
                      </div>
                    ))}
                    <button onClick={() => setBundleItems(items => [...items, { category: "LAPTOP", specs: "" }])} style={{ padding: "5px 12px", border: "1px dashed var(--color-surface-300)", borderRadius: "var(--radius-md)", background: "transparent", color: "var(--color-brand-600)", cursor: "pointer", fontSize: 12, fontWeight: 600, marginBottom: 10 }}>+ Add item</button>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div>
                        <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Needed by date</label>
                        <DatePicker style={inputStyle} value={bundleStartDate} onChange={setBundleStartDate} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Office / Location</label>
                        <input style={inputStyle} value={bundleLocation} onChange={e => setBundleLocation(e.target.value)} placeholder="Bangalore / Remote…" />
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ gridColumn: "1/-1" }}>
                  <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Description</label>
                  <textarea rows={3} style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} value={ticketForm.description} onChange={e => setTicketForm(f => ({ ...f, description: e.target.value }))} placeholder="Steps to reproduce, context…" />
                </div>
              </div>
              {err && <p style={{ color: "var(--color-error)", fontSize: 13, marginBottom: 8 }}>{err}</p>}
              <div style={{ display: "flex", gap: 8 }}>
                <AsyncButton onClick={() => createTicket()}
                  disabled={saving || !ticketForm.title || (ticketForm.ticketType === "ONBOARDING_BUNDLE" && (bundleItems.filter(i => i.category).length === 0 || !ticketForm.employeeId))}
                >Submit Ticket</AsyncButton>
                <button onClick={() => { setShowTicketForm(false); setAssetSearchVal(""); setAssetHits([]); setBundleItems([{ category: "LAPTOP", specs: "" }]); setTicketForEmployee(null); setTicketForm({ ...BLANK_TICKET }); }} style={btnStyle("transparent","var(--color-text-600)")}>Cancel</button>
              </div>
            </div>
          )}

          {/* Ticket filters */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <DropdownSelect
              value={tStatus}
              onChange={setTStatus}
              options={TICKET_STATUS_OPTIONS}
              placeholder="All Statuses"
              ariaLabel="Ticket status"
              open={openFilter === "tStatus"}
              onToggle={() => setOpenFilter(f => (f === "tStatus" ? null : "tStatus"))}
              onClose={() => setOpenFilter(f => (f === "tStatus" ? null : f))}
            />
            <DropdownSelect
              value={tPriority}
              onChange={setTPriority}
              options={TICKET_PRIORITY_OPTIONS}
              placeholder="Any Priority"
              ariaLabel="Ticket priority"
              open={openFilter === "tPriority"}
              onToggle={() => setOpenFilter(f => (f === "tPriority" ? null : "tPriority"))}
              onClose={() => setOpenFilter(f => (f === "tPriority" ? null : f))}
            />
            <DropdownSelect
              value={tType}
              onChange={setTType}
              options={TICKET_TYPE_OPTIONS}
              placeholder="All Types"
              ariaLabel="Ticket type"
              open={openFilter === "tType"}
              onToggle={() => setOpenFilter(f => (f === "tType" ? null : "tType"))}
              onClose={() => setOpenFilter(f => (f === "tType" ? null : f))}
            />
            <Button variant="outline" size="sm" onClick={() => {
              const p = new URLSearchParams();
              if (tStatus)   p.set("status",     tStatus);
              if (tPriority) p.set("priority",   tPriority);
              if (tType)     p.set("ticketType", tType);
              downloadCsv(`/it-tickets/export?${p}`, "it-tickets.csv");
            }}>Export CSV</Button>
          </div>

          {loading ? <p style={{ fontSize: 14, color: "var(--color-text-400)" }}>Loading…</p>
          : tickets.length === 0 ? <p style={{ fontSize: 14, color: "var(--color-text-400)" }}>No tickets found.</p>
          : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {tickets.map(t => {
                const isExpanded = expandedTicket === t.id;
                return (
                  <div key={t.id} style={{ background: "var(--color-surface-0)", border: `1px solid ${isExpanded ? "var(--color-brand-300)" : "var(--color-surface-200)"}`, borderRadius: "var(--radius-lg)" }}>
                    <div onClick={() => {
                      if (isExpanded) { setExpandedTicket(null); setTicketDetail(null); }
                      else { setExpandedTicket(t.id); loadTicketDetail(t.id); }
                    }} style={{ padding: "14px 16px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{t.title}</div>
                        <div style={{ fontSize: 12, color: "var(--color-text-500)" }}>
                          <span style={{ cursor: "pointer" }} onClick={e => { e.stopPropagation(); navigate(`/employees/${t.employee.id}/edit`); }} onMouseEnter={e => (e.currentTarget.style.textDecoration = "underline")} onMouseLeave={e => (e.currentTarget.style.textDecoration = "none")}>{t.employee.firstName} {t.employee.lastName}</span> ({t.employee.employeeCode})
                          {t.linkedAsset && ` · ${t.linkedAsset.assetTag}`}
                          {t.assignedTo && ` · → ${t.assignedTo.firstName} ${t.assignedTo.lastName}`}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--color-text-400)", marginTop: 4 }}>
                          {new Date(t.createdAt).toLocaleDateString("en-IN")}
                          {t._count?.comments ? ` · ${t._count.comments} comment${t._count.comments !== 1 ? "s" : ""}` : ""}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <span style={badge({})}>{TICKET_TYPES.find(x => x.value === t.ticketType)?.label ?? t.ticketType}</span>
                        <span style={badge(PRIORITY_STYLE(t.priority))}>{t.priority}</span>
                        <span style={badge(STATUS_STYLE(t.status))}>{t.status.replace("_"," ")}</span>
                        {t.slaTargetAt && t.status !== "CANCELLED" && (
                          <span style={badge(SLA_COLOR(t.slaTargetAt, t.resolvedAt))}>{slaLabel(t.slaTargetAt, t.resolvedAt)}</span>
                        )}
                      </div>
                    </div>

                    {isExpanded && ticketDetail?.id === t.id && (
                      <div style={{ borderTop: "1px solid var(--color-surface-100)", padding: "14px 16px 60px" }}>
                        {ticketDetail.description && (
                          <p style={{ fontSize: 13, color: "var(--color-text-700)", marginBottom: 12, whiteSpace: "pre-wrap" }}>{ticketDetail.description}</p>
                        )}
                        {ticketDetail.linkedAsset && (
                          <div style={{ fontSize: 12, background: "var(--color-surface-50)", borderRadius: "var(--radius-md)", padding: "6px 10px", marginBottom: 10 }}>
                            Linked asset: <strong>{ticketDetail.linkedAsset.assetTag}</strong> — {ticketDetail.linkedAsset.name}
                          </div>
                        )}
                        {ticketDetail.resolutionNotes && (
                          <div style={{ fontSize: 12, background: "var(--color-success-bg)", borderRadius: "var(--radius-md)", padding: "6px 10px", marginBottom: 10 }}>
                            Resolution: {ticketDetail.resolutionNotes}
                          </div>
                        )}

                        {/* Comments */}
                        {ticketDetail.comments.length > 0 && (
                          <div style={{ marginBottom: 10 }}>
                            {ticketDetail.comments.map(c => (
                              <div key={c.id} style={{ marginBottom: 8, padding: "8px 10px", background: "var(--color-surface-50)", borderRadius: "var(--radius-md)" }}>
                                <div style={{ fontSize: 11, color: "var(--color-text-400)", marginBottom: 3 }}>
                                  {c.author.firstName} {c.author.lastName} · {new Date(c.createdAt).toLocaleString("en-IN")}
                                </div>
                                <div style={{ fontSize: 13 }}>{c.body}</div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Add comment */}
                        <div style={{ display: "flex", gap: 8, marginBottom: isIT ? 12 : 0 }}>
                          <textarea rows={2} style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit", flex: 1 }}
                            value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Add a comment…" />
                          <AsyncButton onClick={() => addComment()} disabled={!newComment.trim()}>Post</AsyncButton>
                        </div>

                        {/* IT action bar */}
                        {isIT && (
                          <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingTop: 10, borderTop: "1px solid var(--color-surface-100)" }}>
                            {["NEW_EQUIPMENT","REPLACEMENT_REPAIR"].includes(ticketDetail.ticketType)
                              && !["RESOLVED","CLOSED","CANCELLED"].includes(ticketDetail.status) && (
                              <div>
                                <label style={{ fontSize: 11, color: "var(--color-text-500)", display: "block", marginBottom: 2 }}>Fulfill with Asset (search available stock)</label>
                                {ticketActionForm.fulfillAssetId ? (
                                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, background: "var(--color-info-bg)", color: "var(--color-info-fg)", borderRadius: "var(--radius-md)", padding: "6px 10px" }}>
                                    {fulfillAssetSearchVal}
                                    <button onClick={() => { setTicketActionForm(f => ({ ...f, fulfillAssetId: "" })); setFulfillAssetSearchVal(""); }}
                                      style={{ background: "none", border: "none", cursor: "pointer", fontWeight: 700, marginLeft: "auto" }}>×</button>
                                  </div>
                                ) : (
                                  <div style={{ position: "relative" }}>
                                    <input style={inputStyle} value={fulfillAssetSearchVal} onChange={e => onFulfillAssetSearch(e.target.value)} placeholder="IT-LT-001 / MacBook…" />
                                    {fulfillAssetHits.length > 0 && (
                                      <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", zIndex: 10 }}>
                                        {fulfillAssetHits.map(a => (
                                          <div key={a.id} onClick={() => { setTicketActionForm(f => ({ ...f, fulfillAssetId: a.id })); setFulfillAssetSearchVal(`${a.assetTag} — ${a.name}`); setFulfillAssetHits([]); }}
                                            style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13 }}>
                                            <strong>{a.assetTag}</strong> — {a.name}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                            {ticketDetail.ticketType === "NEW_EQUIPMENT"
                              && !["RESOLVED","CLOSED","CANCELLED"].includes(ticketDetail.status) && (
                              <div>
                                <button
                                  onClick={() => {
                                    setStockRequestForm({ ...BLANK_STOCK_REQUEST, category: ticketDetail.category, linkedTicketId: ticketDetail.id, officeId: isRegionalScoped ? myOfficeId : "" });
                                    setEditingStockRequestId(null);
                                    setShowStockRequestForm(true);
                                    setTab("procurement");
                                  }}
                                  style={btnStyle("var(--color-surface-100)","var(--color-text-700)")}
                                >
                                  Out of stock — Request Stock
                                </button>
                              </div>
                            )}
                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                              <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, cursor: "pointer" }}>
                                <input type="checkbox" checked={ticketActionForm.assignToMe} onChange={e => setTicketActionForm(f => ({ ...f, assignToMe: e.target.checked }))} />
                                Assign to me
                              </label>
                              <div>
                                <label style={{ fontSize: 11, color: "var(--color-text-500)", display: "block", marginBottom: 2 }}>Status</label>
                                <DropdownSelect
                                  value={ticketActionForm.status}
                                  onChange={v => setTicketActionForm(f => ({ ...f, status: v }))}
                                  options={TICKET_STATUS_OPTIONS}
                                  placeholder="Status"
                                  ariaLabel="Ticket status"
                                  clearable={false}
                                  open={openFilter === "ticketActionStatus"}
                                  onToggle={() => setOpenFilter(f => (f === "ticketActionStatus" ? null : "ticketActionStatus"))}
                                  onClose={() => setOpenFilter(f => (f === "ticketActionStatus" ? null : f))}
                                />
                              </div>
                              <div style={{ flex: 1 }}>
                                <label style={{ fontSize: 11, color: "var(--color-text-500)", display: "block", marginBottom: 2 }}>Resolution Notes</label>
                                <input style={inputStyle} value={ticketActionForm.resolutionNotes} onChange={e => setTicketActionForm(f => ({ ...f, resolutionNotes: e.target.value }))} placeholder="Optional…" />
                              </div>
                              <AsyncButton onClick={() => updateTicketStatus()}>Save</AsyncButton>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── Tab: Procurement ── */}
      {tab === "procurement" && (isIT || isFinance) && (
        <>
          {showStockRequestForm && (
            <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: 20, marginBottom: 20 }}>
              <h3 style={{ fontWeight: 600, fontSize: 15, marginBottom: 14 }}>{editingStockRequestId ? "Edit & Resubmit Stock Request" : "New Stock Request"}</h3>
              {stockRequestForm.linkedTicketId && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, background: "var(--color-brand-50)", border: "1px solid var(--color-brand-200)", borderRadius: "var(--radius-md)", padding: "8px 12px", fontSize: 13 }}>
                  <span style={{ color: "var(--color-text-600)" }}>Linked to IT ticket</span>
                  <button onClick={() => setStockRequestForm(f => ({ ...f, linkedTicketId: "" }))} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "var(--color-brand-600)", marginLeft: "auto" }}>Remove link</button>
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Office *</label>
                  {isRegionalScoped ? (
                    <div style={{ ...inputStyle, background: "var(--color-surface-50)", color: "var(--color-text-500)" }}>
                      {offices.find(o => o.id === myOfficeId)?.name ?? "Your office"}
                    </div>
                  ) : (
                    <DropdownSelect
                      value={stockRequestForm.officeId}
                      onChange={v => setStockRequestForm(f => ({ ...f, officeId: v }))}
                      options={officeOptions}
                      placeholder="Select office…"
                      ariaLabel="Office"
                      clearable={false}
                      className="w-full justify-between"
                      open={openFilter === "srFormOffice"}
                      onToggle={() => setOpenFilter(f => (f === "srFormOffice" ? null : "srFormOffice"))}
                      onClose={() => setOpenFilter(f => (f === "srFormOffice" ? null : f))}
                    />
                  )}
                </div>
                <div>
                  <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Category *</label>
                  <DropdownSelect
                    value={stockRequestForm.category}
                    onChange={v => setStockRequestForm(f => ({ ...f, category: v }))}
                    options={categoryNameOptions}
                    placeholder="Select category…"
                    ariaLabel="Category"
                    clearable={false}
                    className="w-full justify-between"
                    open={openFilter === "srCategory"}
                    onToggle={() => setOpenFilter(f => (f === "srCategory" ? null : "srCategory"))}
                    onClose={() => setOpenFilter(f => (f === "srCategory" ? null : f))}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Model (optional)</label>
                  <input style={inputStyle} value={stockRequestForm.model} onChange={e => setStockRequestForm(f => ({ ...f, model: e.target.value }))} placeholder="Any model, or specify" />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Quantity *</label>
                  <input type="number" min="1" style={inputStyle} value={stockRequestForm.quantity} onChange={e => setStockRequestForm(f => ({ ...f, quantity: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Estimated Unit Cost *</label>
                  <input type="number" step="0.01" style={inputStyle} value={stockRequestForm.estimatedUnitCost} onChange={e => setStockRequestForm(f => ({ ...f, estimatedUnitCost: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Currency</label>
                  <DropdownSelect
                    value={stockRequestForm.currency}
                    onChange={v => setStockRequestForm(f => ({ ...f, currency: v }))}
                    options={CURRENCY_OPTIONS}
                    placeholder="INR"
                    ariaLabel="Currency"
                    clearable={false}
                    className="w-full justify-between"
                    open={openFilter === "srCurrency"}
                    onToggle={() => setOpenFilter(f => (f === "srCurrency" ? null : "srCurrency"))}
                    onClose={() => setOpenFilter(f => (f === "srCurrency" ? null : f))}
                  />
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Justification *</label>
                <textarea rows={3} style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} value={stockRequestForm.justification} onChange={e => setStockRequestForm(f => ({ ...f, justification: e.target.value }))} placeholder="Why is this needed? e.g. current stock exhausted, new hires starting…" />
              </div>
              {err && <p style={{ color: "var(--color-error)", fontSize: 13, marginBottom: 8 }}>{err}</p>}
              <div style={{ display: "flex", gap: 8 }}>
                <AsyncButton
                  onClick={() => submitStockRequest()}
                  disabled={saving || !stockRequestForm.officeId || !stockRequestForm.category || !stockRequestForm.quantity || !stockRequestForm.estimatedUnitCost || !stockRequestForm.justification}
                >
                  {editingStockRequestId ? "Resubmit" : "Submit Request"}
                </AsyncButton>
                <button onClick={() => { setShowStockRequestForm(false); setEditingStockRequestId(null); }} style={btnStyle("transparent","var(--color-text-600)")}>Cancel</button>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <DropdownSelect
              value={srFilterStatus}
              onChange={setSrFilterStatus}
              options={STOCK_REQUEST_STATUS_OPTIONS}
              placeholder="All Statuses"
              ariaLabel="Stock request status"
              open={openFilter === "srStatus"}
              onToggle={() => setOpenFilter(f => (f === "srStatus" ? null : "srStatus"))}
              onClose={() => setOpenFilter(f => (f === "srStatus" ? null : f))}
            />
            {offices.length > 0 && (
              <DropdownSelect
                value={srFilterOffice}
                onChange={setSrFilterOffice}
                options={officeOptions}
                placeholder="All Offices"
                ariaLabel="Office"
                open={openFilter === "srOffice"}
                onToggle={() => setOpenFilter(f => (f === "srOffice" ? null : "srOffice"))}
                onClose={() => setOpenFilter(f => (f === "srOffice" ? null : f))}
              />
            )}
          </div>

          {loading ? <p style={{ fontSize: 14, color: "var(--color-text-400)" }}>Loading…</p>
          : stockRequests.length === 0 ? <p style={{ fontSize: 14, color: "var(--color-text-400)" }}>No stock requests found.</p>
          : (
            <div style={{ overflowX: "auto", background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead><tr style={{ background: "var(--color-surface-50)", borderBottom: "1px solid var(--color-surface-200)" }}>
                  {["Item","Qty","Est. Cost","Office","Requested By","Status","Finance Remarks","Actions"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "10px 14px", color: "var(--color-text-500)", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.4px", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {stockRequests.map((sr, i) => {
                    const totalCost = (parseFloat(sr.estimatedUnitCost) * sr.quantity).toLocaleString("en-IN", { maximumFractionDigits: 2 });
                    const isActingOnThis = financeActionTarget?.id === sr.id;
                    const isReceivingThis = receivingRequestId === sr.id;
                    return (
                      <Fragment key={sr.id}>
                      <tr style={{ borderBottom: (isActingOnThis || isReceivingThis) ? "none" : (i < stockRequests.length - 1 ? "1px solid var(--color-surface-100)" : "none") }}>
                        <td style={{ padding: "10px 14px" }}>
                          <div style={{ fontWeight: 600 }}>{sr.category.replace(/_/g, " ")}</div>
                          {sr.model && <div style={{ fontSize: 11, color: "var(--color-text-400)" }}>{sr.model}</div>}
                          {sr.linkedTicket && <div style={{ fontSize: 11, color: "var(--color-text-400)" }}>from ticket: {sr.linkedTicket.title}</div>}
                          {sr.status === "RECEIVED" && (
                            <div style={{ fontSize: 11, color: "var(--color-text-400)" }}>
                              {sr.vendor && `Vendor: ${sr.vendor} · `}
                              {sr.receivedBy && `Received by ${sr.receivedBy.firstName} ${sr.receivedBy.lastName}`}
                            </div>
                          )}
                          {sr.custodyConfirmedAt && !sr.custodyRevokedAt && (
                            <div style={{ fontSize: 11, color: "var(--color-text-400)" }}>Custody confirmed {new Date(sr.custodyConfirmedAt).toLocaleDateString("en-IN")}</div>
                          )}
                          {sr.custodyRevokedAt && (
                            <div style={{ fontSize: 11, color: "var(--color-error)" }}>Custody revoked {new Date(sr.custodyRevokedAt).toLocaleDateString("en-IN")} — {sr.custodyRevokeReason}</div>
                          )}
                          {sr.invoiceS3Key && (
                            <button onClick={() => viewInvoice(sr.id)} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 11, color: "var(--color-brand-600)", textDecoration: "underline" }}>View Invoice</button>
                          )}
                          {sr.poS3Key && (
                            <button onClick={() => viewPo(sr.id)} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", padding: 0, marginLeft: sr.invoiceS3Key ? 10 : 0, cursor: "pointer", fontSize: 11, color: "var(--color-brand-600)", textDecoration: "underline" }}>View PO</button>
                          )}
                          {sr.status === "RECEIVED" && sr.assetTagPrefix && (
                            <button onClick={() => jumpToReceivedAssets(sr)} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", padding: 0, marginLeft: (sr.invoiceS3Key || sr.poS3Key) ? 10 : 0, cursor: "pointer", fontSize: 11, color: "var(--color-brand-600)", textDecoration: "underline" }}>View {sr.quantity} Asset{sr.quantity !== 1 ? "s" : ""}</button>
                          )}
                        </td>
                        <td style={{ padding: "10px 14px" }}>{sr.quantity}</td>
                        <td style={{ padding: "10px 14px" }}>{sr.currency} {totalCost}</td>
                        <td style={{ padding: "10px 14px", fontSize: 12, color: "var(--color-text-500)" }}>{sr.office.city}</td>
                        <td style={{ padding: "10px 14px", fontSize: 12 }}>{sr.requestedBy.firstName} {sr.requestedBy.lastName}</td>
                        <td style={{ padding: "10px 14px" }}><span style={badge(STATUS_STYLE(sr.status))}>{sr.status.replace(/_/g, " ")}</span></td>
                        <td style={{ padding: "10px 14px", fontSize: 12, color: "var(--color-text-500)", maxWidth: 220 }}>{sr.financeRemarks ?? "—"}</td>
                        <td style={{ padding: "10px 14px" }}>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {isIT && sr.status === "FINANCE_REJECTED" && (
                              <button onClick={() => startEditStockRequest(sr)} style={btnStyle("var(--color-info-bg)","var(--color-info-fg)")}>Edit & Resubmit</button>
                            )}
                            {isIT && sr.status === "SUBMITTED" && (
                              <button onClick={() => { if (confirm("Cancel this request?")) cancelStockRequest(sr.id); }} style={btnStyle("transparent","var(--color-text-600)")}>Cancel</button>
                            )}
                            {isIT && sr.status === "FINANCE_APPROVED" && !isReceivingThis && (
                              <button onClick={() => { setReceivingRequestId(sr.id); setReceiveForm({ vendor: sr.vendor ?? "", poReference: sr.poReference ?? "", assetTagPrefix: "" }); setInvoiceFile(null); setPoFile(null); setVerificationResult(sr.aiVerificationResult); setAiUnavailable(false); setErr(""); }} style={btnStyle("var(--color-info-bg)","var(--color-info-fg)")}>Record Arrival</button>
                            )}
                            {isFinance && sr.status === "SUBMITTED" && !isActingOnThis && (<>
                              <button onClick={() => setFinanceActionTarget({ id: sr.id, action: "approve" })} style={btnStyle("var(--color-success-bg)","var(--color-success-fg)")}>Approve</button>
                              <button onClick={() => setFinanceActionTarget({ id: sr.id, action: "reject" })} style={btnStyle("var(--color-error-bg)","var(--color-error-fg)")}>Reject</button>
                            </>)}
                            {isFinance && sr.status === "RECEIVED" && sr.assetTagPrefix && (!sr.custodyConfirmedAt || sr.custodyRevokedAt) && (
                              <button onClick={() => confirmCustody(sr.id)} style={btnStyle("var(--color-info-bg)","var(--color-info-fg)")}>Confirm Custody</button>
                            )}
                            {isFinance && sr.custodyConfirmedAt && !sr.custodyRevokedAt && !isActingOnThis && (
                              <button onClick={() => setFinanceActionTarget({ id: sr.id, action: "revokeCustody" })} style={btnStyle("var(--color-error-bg)","var(--color-error-fg)")}>Revoke Custody</button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isReceivingThis && (
                        <tr style={{ borderBottom: i < stockRequests.length - 1 ? "1px solid var(--color-surface-100)" : "none" }}>
                          <td colSpan={8} style={{ padding: "10px 14px", background: "var(--color-surface-50)" }}>
                            <div style={{ marginBottom: 10 }}>
                              <label style={{ fontSize: 11, color: "var(--color-text-500)", display: "block", marginBottom: 2 }}>Purchase Order (optional)</label>
                              {sr.poS3Key ? (
                                <button onClick={() => viewPo(sr.id)} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 12, color: "var(--color-brand-600)", textDecoration: "underline" }}>View PO</button>
                              ) : (
                                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                  <FileInput size="sm" accept="application/pdf,image/*" value={poFile} onChange={setPoFile} placeholder="Attach PO" />
                                  <AsyncButton size="sm" variant="outline" animated={false} onClick={() => uploadPo(sr)} disabled={uploadingPo || !poFile}>Upload PO</AsyncButton>
                                </div>
                              )}
                            </div>
                            {!sr.invoiceS3Key ? (<>
                              <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 8 }}>
                                <div>
                                  <label style={{ fontSize: 11, color: "var(--color-text-500)", display: "block", marginBottom: 2 }}>Invoice (required)</label>
                                  <FileInput accept="application/pdf,image/*" value={invoiceFile} onChange={setInvoiceFile} />
                                </div>
                              </div>
                              {err && <p style={{ color: "var(--color-error)", fontSize: 13, marginBottom: 8 }}>{err}</p>}
                              <div style={{ display: "flex", gap: 8 }}>
                                <AsyncButton
                                  onClick={() => uploadInvoice(sr)}
                                  disabled={uploadingInvoice || !invoiceFile}
                                >
                                  Upload Invoice
                                </AsyncButton>
                                <button onClick={() => { setReceivingRequestId(null); setInvoiceFile(null); setPoFile(null); setErr(""); }} style={btnStyle("transparent","var(--color-text-600)")}>Cancel</button>
                              </div>
                            </>) : (<>
                              {(verifying || aiUnavailable || verificationResult) && (
                                <div style={{ marginBottom: 12 }}>
                                  {verifying && <p style={{ fontSize: 12, color: "var(--color-text-400)" }}>Verifying invoice with AI…</p>}
                                  {!verifying && aiUnavailable && <p style={{ fontSize: 12, color: "var(--color-text-400)" }}>AI verification unavailable.</p>}
                                  {!verifying && verificationResult && (
                                    <Card className="p-3">
                                      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: verificationResult.flags.length ? 8 : 0, color: verificationResult.verdict === "FLAGGED" ? "var(--color-warning-fg)" : "var(--color-success-fg)" }}>
                                        {verificationResult.verdict === "FLAGGED"
                                          ? `${verificationResult.flags.length} discrepanc${verificationResult.flags.length !== 1 ? "ies" : "y"} found — review before confirming`
                                          : "Invoice looks consistent with the request"}
                                      </div>
                                      {verificationResult.flags.length > 0 && (
                                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                          {verificationResult.flags.map((f, fi) => (
                                            <div key={fi} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12 }}>
                                              <span style={badge(SEVERITY_STYLE(f.severity))}>{f.severity}</span>
                                              <span style={{ color: "var(--color-text-700)" }}>{f.message}</span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      <button onClick={() => verifyInvoice(sr.id)} disabled={verifying} style={{ background: "none", border: "none", padding: 0, marginTop: 8, cursor: "pointer", fontSize: 11, color: "var(--color-brand-600)", textDecoration: "underline" }}>Re-verify</button>
                                    </Card>
                                  )}
                                </div>
                              )}
                              <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 8 }}>
                                <div>
                                  <label style={{ fontSize: 11, color: "var(--color-text-500)", display: "block", marginBottom: 2 }}>Vendor</label>
                                  <input style={inputStyle} value={receiveForm.vendor} onChange={e => setReceiveForm(f => ({ ...f, vendor: e.target.value }))} placeholder="Optional" />
                                </div>
                                <div>
                                  <label style={{ fontSize: 11, color: "var(--color-text-500)", display: "block", marginBottom: 2 }}>PO Reference</label>
                                  <input style={inputStyle} value={receiveForm.poReference} onChange={e => setReceiveForm(f => ({ ...f, poReference: e.target.value }))} placeholder="Optional" />
                                </div>
                                <div>
                                  <label style={{ fontSize: 11, color: "var(--color-text-500)", display: "block", marginBottom: 2 }}>Asset Tag Prefix (required)</label>
                                  <input style={inputStyle} value={receiveForm.assetTagPrefix} onChange={e => setReceiveForm(f => ({ ...f, assetTagPrefix: e.target.value }))} placeholder="e.g. IT-MON-BLR" />
                                </div>
                              </div>
                              {err && <p style={{ color: "var(--color-error)", fontSize: 13, marginBottom: 8 }}>{err}</p>}
                              <div style={{ display: "flex", gap: 8 }}>
                                <AsyncButton
                                  onClick={() => confirmArrival(sr)}
                                  disabled={saving || !receiveForm.assetTagPrefix.trim()}
                                >
                                  Confirm Arrival
                                </AsyncButton>
                                <button onClick={() => { setReceivingRequestId(null); setPoFile(null); setVerificationResult(null); setAiUnavailable(false); setErr(""); }} style={btnStyle("transparent","var(--color-text-600)")}>Cancel</button>
                              </div>
                            </>)}
                          </td>
                        </tr>
                      )}
                      {isActingOnThis && (
                        <tr style={{ borderBottom: i < stockRequests.length - 1 ? "1px solid var(--color-surface-100)" : "none" }}>
                          <td colSpan={8} style={{ padding: "10px 14px", background: "var(--color-surface-50)" }}>
                            <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                              <div style={{ flex: 1, minWidth: 240 }}>
                                <label style={{ fontSize: 11, color: "var(--color-text-500)", display: "block", marginBottom: 2 }}>
                                  Remarks{(financeActionTarget?.action === "reject" || financeActionTarget?.action === "revokeCustody") ? " (required)" : " (optional)"}
                                </label>
                                <input style={inputStyle} value={financeRemarksDraft[sr.id] ?? ""} onChange={e => setFinanceRemarksDraft(f => ({ ...f, [sr.id]: e.target.value }))}
                                  placeholder={
                                    financeActionTarget?.action === "reject" ? "Why is this being rejected?"
                                    : financeActionTarget?.action === "revokeCustody" ? "Why is custody being revoked? (e.g. physical audit finding)"
                                    : "Optional note…"
                                  } />
                              </div>
                              <AsyncButton
                                onClick={() => financeActionTarget?.action === "approve" ? financeApprove(sr.id) : financeActionTarget?.action === "reject" ? financeReject(sr.id) : revokeCustody(sr.id)}
                                disabled={saving || ((financeActionTarget?.action === "reject" || financeActionTarget?.action === "revokeCustody") && !(financeRemarksDraft[sr.id] ?? "").trim())}
                                variant={financeActionTarget?.action === "approve" ? "success" : "destructive"}
                              >
                                Confirm {financeActionTarget?.action === "approve" ? "Approval" : financeActionTarget?.action === "reject" ? "Rejection" : "Revoke"}
                              </AsyncButton>
                              <button onClick={() => setFinanceActionTarget(null)} style={btnStyle("transparent","var(--color-text-600)")}>Cancel</button>
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Tab: Import ── */}
      {tab === "import" && isIT && (
        <AssetImportPanel offices={offices} isRegionalScoped={isRegionalScoped} myOfficeId={myOfficeId} />
      )}

      {tab === "disposals" && (isIT || isFinance) && (
        <AssetDisposalPanel
          eligibleAssets={assets}
          prefillAssetId={disposalPrefillAssetId}
          onPrefillConsumed={() => setDisposalPrefillAssetId(null)}
          isFinance={isFinance}
          isRegionalScoped={isRegionalScoped}
          myOfficeId={myOfficeId}
        />
      )}

      {/* ── Tab: IT Admin ── */}
      {tab === "admin" && isIT && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* ── Onboarding Queue (HRMS-0071) ── */}
          <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: 16 }}>
            <h3 style={{ fontWeight: 600, fontSize: 15, marginBottom: 12 }}>Onboarding Queue</h3>
            {onboardingLoading ? <p style={{ fontSize: 13, color: "var(--color-text-400)" }}>Loading…</p> : (() => {
              const bundleEmpIds = new Set(bundleTickets.map(t => t.employee.id));
              const needsKit = onboardingEmps.filter(e => !bundleEmpIds.has(e.id));
              const inFlight  = bundleTickets.filter(t => !["RESOLVED","CLOSED","CANCELLED"].includes(t.status));
              if (needsKit.length === 0 && inFlight.length === 0) {
                return <p style={{ fontSize: 13, color: "var(--color-text-400)" }}>No onboarding employees right now.</p>;
              }
              return (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", color: "var(--color-text-500)", marginBottom: 8 }}>
                      Needs IT Kit ({needsKit.length})
                    </div>
                    <div style={{ maxHeight: 320, overflowY: "auto" }}>
                      {needsKit.length === 0
                        ? <p style={{ fontSize: 13, color: "var(--color-success-fg)" }}>All onboarding employees have kit requests in flight.</p>
                        : needsKit.map(e => (
                          <div key={e.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", background: "var(--color-surface-50)", borderRadius: "var(--radius-md)", marginBottom: 6 }}>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>{e.firstName} {e.lastName}</div>
                              <div style={{ fontSize: 11, color: "var(--color-text-400)", fontFamily: "monospace" }}>{e.employeeCode}</div>
                            </div>
                            <button
                              onClick={() => {
                                setShowTicketForm(true);
                                setTicketForm(f => ({ ...f, ticketType: "ONBOARDING_BUNDLE", employeeId: e.id, title: `Onboarding kit — ${e.firstName} ${e.lastName}` }));
                                setTicketForEmployee({ firstName: e.firstName, lastName: e.lastName, employeeCode: e.employeeCode });
                                setTab("tickets");
                              }}
                              style={btnStyle()}>
                              Raise Kit Request
                            </button>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", color: "var(--color-text-500)", marginBottom: 8 }}>
                      In Flight ({inFlight.length})
                    </div>
                    <div style={{ maxHeight: 320, overflowY: "auto" }}>
                      {inFlight.length === 0
                        ? <p style={{ fontSize: 13, color: "var(--color-text-400)" }}>No open onboarding bundle tickets.</p>
                        : inFlight.map(t => (
                          <div key={t.id} style={{ padding: "8px 10px", background: "var(--color-surface-50)", borderRadius: "var(--radius-md)", marginBottom: 6 }}>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{t.employee.firstName} {t.employee.lastName}</div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                              <span style={{ fontSize: 11, color: "var(--color-text-400)" }}>{new Date(t.createdAt).toLocaleDateString("en-IN")}</span>
                              <span style={badge(STATUS_STYLE(t.status))}>{t.status.replace("_"," ")}</span>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* ── Warranty / AMC Renewal Alerts (HRMS-0073) ── */}
          <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: 16 }}>
            <h3 style={{ fontWeight: 600, fontSize: 15, marginBottom: 12 }}>Warranty & AMC Renewals (next 90 days)</h3>
            {!expiringData ? (
              <p style={{ fontSize: 13, color: "var(--color-text-400)" }}>Loading…</p>
            ) : (() => {
              const hardware = [...(expiringData.expired), ...(expiringData.expiringSoon)].filter(a => a.category !== "SOFTWARE_LICENSE");
              const software = [...(expiringData.expired), ...(expiringData.expiringSoon)].filter(a => a.category === "SOFTWARE_LICENSE");
              const ExpiryTable = ({ items, label }: { items: Asset[]; label: string }) => (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", color: "var(--color-text-500)", marginBottom: 8 }}>{label}</div>
                  {items.length === 0
                    ? <p style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13, color: "var(--color-success-fg)" }}>No renewals due within 90 days <CheckCircle2 size={14} /></p>
                    : (
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                          <thead><tr style={{ background: "var(--color-surface-50)" }}>
                            {["Tag","Name","Category","Expiry","Status"].map(h => (
                              <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "var(--color-text-500)", textTransform: "uppercase", letterSpacing: "0.4px", whiteSpace: "nowrap" }}>{h}</th>
                            ))}
                          </tr></thead>
                          <tbody>
                            {items.map((a, i) => {
                              const days = a.warrantyExpiry ? Math.ceil((new Date(a.warrantyExpiry).getTime() - Date.now()) / 86_400_000) : null;
                              const rowBg = days !== null && days < 0 ? "var(--color-error-bg-subtle)" : days !== null && days < 30 ? "var(--color-warning-bg-subtle)" : days !== null && days < 90 ? "var(--color-warning-bg-subtle)" : "transparent";
                              const dayLabel = days === null ? "—" : days < 0 ? `Expired ${Math.abs(days)}d ago` : `${days}d left`;
                              const dayColor = days !== null && days < 0 ? "var(--color-error-fg)" : days !== null && days < 30 ? "var(--color-warning-fg-strong)" : "var(--color-warning-fg)";
                              return (
                                <tr key={a.id} style={{ background: rowBg, borderTop: i > 0 ? "1px solid var(--color-surface-100)" : "none" }}>
                                  <td style={{ padding: "8px 12px", fontFamily: "monospace", fontSize: 12 }}>{a.assetTag}</td>
                                  <td style={{ padding: "8px 12px", fontWeight: 600 }}>{a.name}</td>
                                  <td style={{ padding: "8px 12px", fontSize: 12, color: "var(--color-text-400)" }}>{a.category}</td>
                                  <td style={{ padding: "8px 12px", fontSize: 12, fontWeight: 600, color: dayColor }}>{dayLabel}</td>
                                  <td style={{ padding: "8px 12px" }}><span style={badge(STATUS_STYLE(a.status))}>{a.status.replace("_"," ")}</span></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )
                  }
                </div>
              );
              return (
                <>
                  <ExpiryTable items={hardware} label="Hardware Warranty" />
                  <ExpiryTable items={software} label="Software License Renewals" />
                </>
              );
            })()}
          </div>

          {/* ── Status Guide ── */}
          <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: 16 }}>
            <h3 style={{ fontWeight: 600, fontSize: 15, marginBottom: 10 }}>Status Guide</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8 }}>
              {[
                ["ON_ORDER → RECEIVED","Asset physically arrives"],
                ["RECEIVED → IN_FINANCE_CUSTODY","Finance confirms custody"],
                ["IN_FINANCE_CUSTODY → AVAILABLE","IT accepts handoff"],
                ["AVAILABLE → Assign","Issue via Inventory tab"],
                ["ASSIGNED → Transfer","Move to another employee"],
                ["ASSIGNED → Return","Return via Inventory tab"],
                ["RETURNED → AVAILABLE","Mark available after inspect"],
                ["Any → IN_REPAIR","Sent for servicing"],
                ["IN_REPAIR → AVAILABLE","Returned from service"],
                ...(isIT ? [["RETIRED/LOST/STOLEN → DISPOSED","Via approved disposal request"]] : []),
              ].map(([title, desc]) => (
                <div key={title} style={{ background: "var(--color-surface-50)", borderRadius: "var(--radius-md)", padding: "8px 12px" }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{title}</div>
                  <div style={{ fontSize: 11, color: "var(--color-text-500)" }}>{desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Assign Modal ── */}
      {assigningAsset && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)", padding: 24, width: 420, maxWidth: "90vw", boxShadow: "var(--shadow-xl)" }}>
            <h3 style={{ fontWeight: 700, fontSize: 16, marginBottom: 14 }}>Assign {assigningAsset.assetTag}</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Search Employee *</label>
              <div style={{ position: "relative" }}>
                <input style={inputStyle} value={empSearch} onChange={e => onEmpSearch(e.target.value)} placeholder="Name or employee code…" />
                {empLoading && <div style={{ position: "absolute", right: 8, top: 10, fontSize: 11, color: "var(--color-text-400)" }}>…</div>}
                {empHits.length > 0 && (
                  <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", zIndex: 10 }}>
                    {empHits.map(e => (
                      <div key={e.id} onClick={() => { setAssignForm(f => ({ ...f, employeeId: e.id })); setEmpSearch(`${e.firstName} ${e.lastName} (${e.employeeCode})`); setEmpHits([]); }}
                        style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13 }}>
                        {e.firstName} {e.lastName} — <span style={{ fontFamily: "monospace", fontSize: 11 }}>{e.employeeCode}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Condition at Issue</label>
              <DropdownSelect
                value={assignForm.conditionAtIssue}
                onChange={v => setAssignForm(f => ({ ...f, conditionAtIssue: v }))}
                options={CONDITION_OPTIONS}
                placeholder="Condition"
                ariaLabel="Condition at issue"
                clearable={false}
                className="w-full justify-between"
                open={openFilter === "assignCondition"}
                onToggle={() => setOpenFilter(f => (f === "assignCondition" ? null : "assignCondition"))}
                onClose={() => setOpenFilter(f => (f === "assignCondition" ? null : f))}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Expected Return Date (optional)</label>
              <DatePicker style={inputStyle} value={assignForm.expectedReturnDate} onChange={v => setAssignForm(f => ({ ...f, expectedReturnDate: v }))} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Notes</label>
              <input style={inputStyle} value={assignForm.notes} onChange={e => setAssignForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Before Photo (optional)</label>
              <FileInput accept="image/*" capture="environment" placeholder="Add photo" value={beforePhotoFile} onChange={setBeforePhotoFile} />
            </div>
            {err && <p style={{ color: "var(--color-error)", fontSize: 13, marginBottom: 8 }}>{err}</p>}
            <div style={{ display: "flex", gap: 8 }}>
              <AsyncButton onClick={() => doAssign()} disabled={saving || !assignForm.employeeId}>Assign</AsyncButton>
              <button onClick={() => { setAssigningAsset(null); setBeforePhotoFile(null); }} style={btnStyle("transparent","var(--color-text-600)")}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Return Modal ── */}
      {returningAsset && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)", padding: 24, width: 380, maxWidth: "90vw", boxShadow: "var(--shadow-xl)" }}>
            <h3 style={{ fontWeight: 700, fontSize: 16, marginBottom: 14 }}>Return {returningAsset.assetTag}</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Condition at Return</label>
              <DropdownSelect
                value={returnForm.conditionAtReturn}
                onChange={v => setReturnForm(f => ({ ...f, conditionAtReturn: v }))}
                options={CONDITION_OPTIONS}
                placeholder="Condition"
                ariaLabel="Condition at return"
                clearable={false}
                className="w-full justify-between"
                open={openFilter === "returnCondition"}
                onToggle={() => setOpenFilter(f => (f === "returnCondition" ? null : "returnCondition"))}
                onClose={() => setOpenFilter(f => (f === "returnCondition" ? null : f))}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Return Notes</label>
              <textarea rows={2} style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} value={returnForm.returnNotes} onChange={e => setReturnForm(f => ({ ...f, returnNotes: e.target.value }))} placeholder="Any damage, missing accessories…" />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>After Photo (optional)</label>
              <FileInput accept="image/*" capture="environment" placeholder="Add photo" value={afterPhotoFile} onChange={setAfterPhotoFile} />
            </div>
            {err && <p style={{ color: "var(--color-error)", fontSize: 13, marginBottom: 8 }}>{err}</p>}
            <div style={{ display: "flex", gap: 8 }}>
              <AsyncButton onClick={() => doReturn()} disabled={saving}>Confirm Return</AsyncButton>
              <button onClick={() => { setReturningAsset(null); setAfterPhotoFile(null); }} style={btnStyle("transparent","var(--color-text-600)")}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Transfer Modal ── */}
      {transferringAsset && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)", padding: 24, width: 420, maxWidth: "90vw", boxShadow: "var(--shadow-xl)" }}>
            <h3 style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Transfer {transferringAsset.assetTag}</h3>
            {transferringAsset.assignments[0]?.employee && (
              <p style={{ fontSize: 12, color: "var(--color-text-500)", marginBottom: 14 }}>
                Currently with {transferringAsset.assignments[0].employee.firstName} {transferringAsset.assignments[0].employee.lastName}
              </p>
            )}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Transfer To *</label>
              <div style={{ position: "relative" }}>
                <input style={inputStyle} value={empSearch} onChange={e => onEmpSearch(e.target.value)} placeholder="Name or employee code…" />
                {empLoading && <div style={{ position: "absolute", right: 8, top: 10, fontSize: 11, color: "var(--color-text-400)" }}>…</div>}
                {empHits.length > 0 && (
                  <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", zIndex: 10 }}>
                    {empHits.map(e => (
                      <div key={e.id} onClick={() => { setTransferForm(f => ({ ...f, employeeId: e.id })); setEmpSearch(`${e.firstName} ${e.lastName} (${e.employeeCode})`); setEmpHits([]); }}
                        style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13 }}>
                        {e.firstName} {e.lastName} — <span style={{ fontFamily: "monospace", fontSize: 11 }}>{e.employeeCode}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Condition at Return</label>
                <DropdownSelect
                  value={transferForm.conditionAtReturn}
                  onChange={v => setTransferForm(f => ({ ...f, conditionAtReturn: v }))}
                  options={CONDITION_OPTIONS}
                  placeholder="Condition"
                  ariaLabel="Condition at return"
                  clearable={false}
                  className="w-full justify-between"
                  open={openFilter === "transferReturn"}
                  onToggle={() => setOpenFilter(f => (f === "transferReturn" ? null : "transferReturn"))}
                  onClose={() => setOpenFilter(f => (f === "transferReturn" ? null : f))}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Condition at Issue</label>
                <DropdownSelect
                  value={transferForm.conditionAtIssue}
                  onChange={v => setTransferForm(f => ({ ...f, conditionAtIssue: v }))}
                  options={CONDITION_OPTIONS}
                  placeholder="Condition"
                  ariaLabel="Condition at issue"
                  clearable={false}
                  className="w-full justify-between"
                  open={openFilter === "transferIssue"}
                  onToggle={() => setOpenFilter(f => (f === "transferIssue" ? null : "transferIssue"))}
                  onClose={() => setOpenFilter(f => (f === "transferIssue" ? null : f))}
                />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Expected Return Date (optional)</label>
              <DatePicker style={inputStyle} value={transferForm.expectedReturnDate} onChange={v => setTransferForm(f => ({ ...f, expectedReturnDate: v }))} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Notes</label>
              <input style={inputStyle} value={transferForm.notes} onChange={e => setTransferForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            {err && <p style={{ color: "var(--color-error)", fontSize: 13, marginBottom: 8 }}>{err}</p>}
            <div style={{ display: "flex", gap: 8 }}>
              <AsyncButton onClick={() => doTransfer()} disabled={saving || !transferForm.employeeId}>Transfer</AsyncButton>
              <button onClick={() => setTransferringAsset(null)} style={btnStyle("transparent","var(--color-text-600)")}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
